import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { DbFilterConfigPanel } from "../components/DbFilterConfigPanel";
import { api, formatError, API } from "../lib/api";
import { toast } from "sonner";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Upload, Trash2, ExternalLink, Save, Mail, Send, ShieldCheck, Star, Pin, Plus, Pencil, X, FileUp, Download, Loader2, History, RefreshCw, FileWarning, Eye, Tag, Calendar, Megaphone, Link2, Copy, CheckCircle2, EyeOff, Clock, Gift, QrCode, Ban, GraduationCap, Sparkles, Wand2, Rocket, Layers } from "lucide-react";
import { EXTERNAL_REF_SOURCES } from "../lib/externalRefs";
import { IntrosConsole, WalkthroughsConsole } from "../components/AdminContentConsoles";
import CMSConsole from "../components/cms/CMSConsole";
import WorksheetsConsole from "../components/WorksheetsConsole";
import ModulesConsole from "../components/ModulesConsole";
import KnowledgeConsole from "../components/KnowledgeConsole";
import AdminMenuGrid from "../components/AdminMenuGrid";

export default function Admin() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "users";
  const [tab, setTabRaw] = useState(initialTab);
  const setTab = (t) => {
    setTabRaw(t);
    setSearchParams((prev) => { const np = new URLSearchParams(prev); np.set("tab", t); return np; }, { replace: true });
  };
  const [users, setUsers] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [modules, setModules] = useState([]);
  const [stories, setStories] = useState([]);
  const [drafts, setDrafts] = useState({});

  const loadUsers = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  const loadDownloads = () => api.get("/downloads").then((r) => setDownloads(r.data)).catch(() => {});
  const loadModules = () => api.get("/modules").then((r) => setModules(r.data)).catch(() => {});
  const loadStories = () => api.get("/admin/stories").then((r) => setStories(r.data)).catch(() => {});

  useEffect(() => {
    loadUsers(); loadDownloads(); loadModules(); loadStories();
  }, []);

  const ADMIN_ROLES = ["super_admin", "admin", "contributor", "tester"];
  if (user && !ADMIN_ROLES.includes((user.role || "").toLowerCase())) return <Navigate to="/dashboard" replace />;

  const setPlan = async (uid, slug) => {
    try {
      await api.post(`/admin/users/${uid}/package`, { package_slug: slug, package_status: slug ? "active" : null });
      toast.success("Plan updated"); loadUsers();
    } catch (e) { toast.error(formatError(e)); }
  };

  const deleteUser = async (u) => {
    // Hard delete: user + sessions + progress + entitlements + grants.
    // Confirms before firing since it's irreversible. Intended for QA
    // (re-testing Google sign-in with the same email, etc.).
    if (!window.confirm(
      `Delete ${u.email}?\n\n`
      + "This wipes their account, sessions, progress, grants, and usage counters. "
      + "The email becomes available for a fresh signup — good for testing Google login flows.\n\n"
      + "This action cannot be undone."
    )) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`Deleted ${u.email}`);
      loadUsers();
    } catch (e) { toast.error(formatError(e)); }
  };

  const upload = async (form) => {
    const fd = new FormData(form);
    try {
      const res = await fetch(`${API}/admin/downloads`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Upload failed");
      }
      toast.success("Uploaded");
      form.reset(); loadDownloads();
    } catch (e) { toast.error(e.message || "Upload failed"); }
  };

  const deleteDownload = async (id) => {
    try { await api.delete(`/admin/downloads/${id}`); loadDownloads(); toast.success("Deleted"); }
    catch (e) { toast.error(formatError(e)); }
  };

  const saveModule = async (slug) => {
    const d = drafts[slug];
    if (!d) return;
    try {
      await api.post(`/admin/modules/${slug}`, d);
      toast.success("Module saved"); loadModules();
    } catch (e) { toast.error(formatError(e)); }
  };

  const moderate = async (id, patch) => {
    try { await api.post(`/admin/stories/${id}`, patch); await loadStories(); toast.success("Story updated"); }
    catch (e) { toast.error(formatError(e)); }
  };
  const removeStory = async (id) => {
    try { await api.delete(`/stories/${id}`); await loadStories(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <AppShell>
      <div className="w-full">
        <PageHero
          eyebrow="Warren Team"
          icon={ShieldCheck}
          title={<>Admin <span className="text-grad">Console</span></>}
          subtitle="Manage users, downloads, course modules, stories, blueprints, and databases."
          right={null}
          testid="admin-hero"
        />
      </div>

      <div className="w-full">
        <AdminMenuGrid active={tab} onSelect={setTab} />
      </div>

      {tab === "users" && (
        <UsersConsole users={users} setPlan={setPlan} deleteUser={deleteUser} />
      )}

      {tab === "plans" && (
        <PlansConsole />
      )}

      {tab === "roles" && user?.role === "super_admin" && <AdminRolesConsole />}

      {tab === "downloads" && (
        <div className="grid lg:grid-cols-[380px_1fr] gap-6 min-w-0">
          <form
            className="card-elevated p-6 h-fit min-w-0"
            onSubmit={(e) => { e.preventDefault(); upload(e.currentTarget); }}
            data-testid="admin-upload-form"
          >
            <p className="font-display text-2xl">Upload a download</p>
            <p className="text-xs text-muted-foreground mt-1">PDF, CSV, or XLSX up to ~20 MB.</p>
            <div className="mt-4 space-y-3">
              <input name="title" required placeholder="Title" data-testid="admin-upload-title"
                className="w-full rounded-xl border border-border px-3 py-2 text-sm" />
              <textarea name="description" placeholder="Short description" rows={3} data-testid="admin-upload-desc"
                className="w-full rounded-xl border border-border px-3 py-2 text-sm" />
              <select name="min_tier" defaultValue="1" data-testid="admin-upload-tier"
                className="w-full rounded-xl border border-border px-3 py-2 text-sm">
                <option value="1">Starter</option>
                <option value="2">Growth</option>
                <option value="3">Full Access</option>
              </select>
              <input type="file" name="file" required accept=".pdf,.csv,.xlsx" data-testid="admin-upload-file"
                className="w-full max-w-full text-xs sm:text-sm file:mr-2 file:px-2 file:py-1 file:rounded file:border file:border-border file:bg-muted file:text-xs" />
            </div>
            <button type="submit" className="btn-primary mt-5 w-full justify-center" data-testid="admin-upload-submit">
              <Upload className="w-4 h-4" /> Upload
            </button>
          </form>

          <div className="min-w-0">
            {downloads.length === 0 ? (
              <div className="card-elevated p-10 text-center text-muted-foreground">No downloads yet.</div>
            ) : (
              <div className="card-elevated admin-scroll-x min-w-0">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-muted text-left">
                    <tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:uppercase [&>th]:text-xs [&>th]:tracking-widest">
                      <th>Title</th><th>Plan</th><th>Type</th><th>Size</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {downloads.map((d) => (
                      <tr key={d.id} data-testid={`admin-download-row-${d.id}`} className="border-t border-border">
                        <td className="px-4 py-3 font-semibold">{d.title}</td>
                        <td className="px-4 py-3">{({1:"Starter",2:"Growth",3:"Full Access"})[d.min_tier] || "—"}</td>
                        <td className="px-4 py-3 uppercase text-xs">{d.ext}</td>
                        <td className="px-4 py-3">{Math.round((d.size || 0) / 1024)} KB</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => deleteDownload(d.id)} data-testid={`admin-download-delete-${d.id}`}
                            className="p-2 hover:bg-muted rounded">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "modules" && <ModulesConsole modules={modules} reloadModules={loadModules} />}

      {tab === "stories" && (
        <div className="grid gap-3">
          <StoryOfWeekPanel />
          {stories.length === 0 ? (
            <div className="card-elevated p-10 text-center text-muted-foreground">No stories submitted yet.</div>
          ) : stories.map((s) => (
            <div key={s.id} className="card-elevated p-5" data-testid={`admin-story-${s.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display text-xl">{s.brand}</p>
                    {s.approved ? (
                      <span className="text-[10px] uppercase tracking-widest bg-green-100 text-green-700 rounded px-2 py-0.5 font-semibold">Approved</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest bg-pink-100 text-pink-700 rounded px-2 py-0.5 font-semibold">Pending</span>
                    )}
                    {s.featured && <span className="text-[10px] uppercase tracking-widest bg-primary/15 text-primary rounded px-2 py-0.5 font-semibold">Featured</span>}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">"{s.story}"</p>
                  <p className="mt-2 text-xs text-muted-foreground">
                    {s.author_name}{s.website ? " · " : ""}
                    {s.website && <a href={s.website} target="_blank" rel="noreferrer" className="text-primary">{s.website}</a>}
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {!s.approved && (
                    <button onClick={() => moderate(s.id, { approved: true })} data-testid={`admin-story-approve-${s.id}`}
                      className="btn-amber text-xs py-1.5">Approve</button>
                  )}
                  <button onClick={() => moderate(s.id, { featured: !s.featured })} data-testid={`admin-story-feature-${s.id}`}
                    className="text-xs font-semibold text-shell-side border border-shell-border rounded-md px-3 py-1.5">
                    {s.featured ? "Unfeature" : "Feature"}
                  </button>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/stories/${s.id}/instagram-card`} target="_blank" rel="noreferrer"
                    className="text-xs font-semibold text-primary" data-testid={`admin-story-ig-${s.id}`}>
                    Instagram card
                  </a>
                  <button onClick={() => removeStory(s.id)} className="text-xs text-muted-foreground hover:text-destructive"
                    data-testid={`admin-story-delete-${s.id}`}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "blueprints" && <BlueprintQueue />}
      {tab === "databases" && <DatabaseImporter />}
      {tab === "industry" && <IndustryVendorEditor />}
      {tab === "deals" && <DealsConsole />}
      {tab === "access" && <AccessCodesConsole />}
      {tab === "students" && <StudentApplicationsConsole />}
      {/* Intros + Walkthroughs are now sub-tabs inside the "Content CMS"
       * top-level tab; keeping the routes here as hidden fallbacks so
       * bookmarks / older admin links still work. */}
      {tab === "intros" && <IntrosConsole />}
      {tab === "walkthroughs" && <WalkthroughsConsole />}
      {tab === "cms" && <CMSConsole />}
      {tab === "worksheets" && <WorksheetsConsole />}
      {tab === "knowledge" && <KnowledgeConsole />}
    </AppShell>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Users & Plans — table with search + role/plan/status filter
 * Search matches name or email (case-insensitive). Filters are AND-combined.
 * Results and filter chips are visible above the table.
 * ─────────────────────────────────────────────────────────────────── */
function UsersConsole({ users, setPlan, deleteUser }) {
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [plan, setPlanFilter] = useState("");
  const [status, setStatus] = useState("");

  const filtered = React.useMemo(() => {
    const query = q.trim().toLowerCase();
    return (users || []).filter((u) => {
      if (query) {
        const hay = `${u.name || ""} ${u.email || ""}`.toLowerCase();
        if (!hay.includes(query)) return false;
      }
      if (role && (u.role || "").toLowerCase() !== role) return false;
      if (plan && (u.package_slug || "") !== plan) return false;
      if (status && (u.package_status || "") !== status) return false;
      return true;
    });
  }, [users, q, role, plan, status]);

  const clearAll = () => { setQ(""); setRole(""); setPlanFilter(""); setStatus(""); };
  const activeFilters = [role, plan, status].filter(Boolean).length + (q ? 1 : 0);
  const planCounts = React.useMemo(() => {
    const c = { free: 0, founder: 0, pro: 0, none: 0 };
    (users || []).forEach((u) => {
      const k = u.package_slug || "none";
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [users]);

  return (
    <div className="space-y-4">
      {/* Search + filter bar */}
      <div className="card-elevated p-4 flex flex-wrap items-end gap-3" data-testid="admin-users-filters">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Search</label>
          <div className="mt-1 relative">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              className="w-full rounded-lg border border-border bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-primary/40"
              data-testid="admin-users-search"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">🔍</span>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mt-1 rounded-lg border border-border bg-white px-3 py-2 text-sm min-w-[130px]"
            data-testid="admin-users-filter-role"
          >
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="founder">Founder</option>
            <option value="member">Member</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Plan</label>
          <select
            value={plan}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="mt-1 rounded-lg border border-border bg-white px-3 py-2 text-sm min-w-[180px]"
            data-testid="admin-users-filter-plan"
          >
            <option value="">All plans</option>
            <option value="free">Free Access ({planCounts.free})</option>
            <option value="founder">Founder Membership ({planCounts.founder})</option>
            <option value="pro">Founder Pro ({planCounts.pro})</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Status</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 rounded-lg border border-border bg-white px-3 py-2 text-sm min-w-[130px]"
            data-testid="admin-users-filter-status"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="past_due">Past due</option>
            <option value="canceled">Canceled</option>
            <option value="incomplete">Incomplete</option>
          </select>
        </div>
        {activeFilters > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="rounded-lg bg-muted hover:bg-border/60 border border-border px-3 py-2 text-xs font-semibold"
            data-testid="admin-users-clear"
          >
            Clear ({activeFilters})
          </button>
        )}
        <div className="ml-auto text-xs text-muted-foreground">
          Showing <span className="font-bold" data-testid="admin-users-count">{filtered.length}</span> of {users.length}
        </div>
      </div>

      {/* Table — horizontal scroll on narrow viewports so the rightmost
          Delete column is always reachable without CSS clipping. The
          `min-w-[860px]` on the table keeps columns from squishing
          into unreadable widths at the same time. */}
      <div className="card-elevated admin-scroll-x">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="bg-muted text-left">
            <tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:uppercase [&>th]:text-xs [&>th]:tracking-widest">
              <th>Name</th><th>Email</th><th>Role</th><th>Plan</th><th>Status</th><th className="w-24 text-right">Delete</th><th>Set plan</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground" data-testid="admin-users-empty">
                  No users match the current filters.
                </td>
              </tr>
            )}
            {filtered.map((u) => (
              <tr key={u.user_id} data-testid={`admin-row-${u.user_id}`} className="border-t border-border">
                <td className="px-4 py-3 font-semibold whitespace-nowrap">{u.name}</td>
                <td className="px-4 py-3 whitespace-nowrap">{u.email}</td>
                <td className="px-4 py-3 capitalize whitespace-nowrap">{u.role}</td>
                <td className="px-4 py-3 whitespace-nowrap">{u.package_slug || "—"}</td>
                <td className="px-4 py-3 whitespace-nowrap">{u.package_status || "—"}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => deleteUser(u)}
                    data-testid={`admin-delete-user-${u.user_id}`}
                    title={`Delete ${u.email}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 text-red-600 hover:bg-red-600 hover:text-white hover:border-red-600 px-2.5 py-1.5 text-xs font-semibold transition"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span className="hidden md:inline">Delete</span>
                  </button>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={u.package_slug || ""}
                    onChange={(e) => setPlan(u.user_id, e.target.value || null)}
                    data-testid={`admin-plan-select-${u.user_id}`}
                    className="rounded-md bg-white border border-border px-2 py-1 text-sm"
                  >
                    <option value="">— none —</option>
                    <option value="free">Free Access</option>
                    <option value="founder">Founder Membership</option>
                    <option value="pro">Founder Pro Membership</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Plans Console — summary cards for every subscription plan. "Edit" opens
// the Plan Builder at /admin/plan-builder/{slug}.
// ---------------------------------------------------------------------------
function PlansConsole() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/plans");
      setPlans(data.plans || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset every plan to its baseline? All overrides will be dropped.")) return;
    try {
      await api.post("/admin/plans/reset");
      toast.success("All plans reset to baseline.");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading plans…</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-display text-2xl">Subscription plans</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            Edit copy, pricing, and features for the plans shown on the pricing page + account upgrade grid.
            Changes go live immediately — Stripe products stay linked by slug so subscriptions aren't disrupted.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={resetAll}
            className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-muted"
            data-testid="admin-plans-reset-all"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reset all
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="admin-plans-grid">
        {plans.map((p) => {
          const eff = p.effective;
          const overridden = Object.keys(p.overrides || {});
          return (
            <div
              key={eff.slug}
              className={["card-elevated p-5 flex flex-col relative overflow-hidden",
                eff.featured ? "ring-2 ring-primary/30" : ""].join(" ")}
              data-testid={`admin-plan-card-${eff.slug}`}
            >
              {eff.featured && (
                <span className="absolute top-3 right-3 text-[9.5px] font-bold uppercase tracking-widest text-primary">
                  Featured
                </span>
              )}
              <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                {eff.eyebrow || "\u00A0"}
              </p>
              <p className="font-display text-2xl mt-1">{eff.name}</p>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-3">{eff.tagline}</p>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-bold">${eff.monthly}</span>
                <span className="text-sm text-muted-foreground">/ mo</span>
                {eff.yearly > 0 && (
                  <span className="text-xs text-muted-foreground ml-2">${eff.yearly}/yr</span>
                )}
              </div>
              {eff.enrollment_fee > 0 && (
                <p className="text-xs text-muted-foreground mt-1">+ ${eff.enrollment_fee} enrollment</p>
              )}
              <div className="mt-3 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                <span><b className="text-foreground">{eff.features?.length || 0}</b> features</span>
                <span><b className="text-foreground">{eff.excludes?.length || 0}</b> excludes</span>
              </div>
              {overridden.length > 0 && (
                <p
                  className="mt-3 text-[10px] font-semibold uppercase tracking-widest text-pink-600"
                  data-testid={`admin-plan-overrides-${eff.slug}`}
                  title={`Overridden: ${overridden.join(", ")}`}
                >
                  {overridden.length} field{overridden.length === 1 ? "" : "s"} customized
                </p>
              )}
              <div className="mt-auto pt-4 flex items-center gap-2">
                <Link
                  to={`/admin/plan-builder/${eff.slug}`}
                  className="btn-primary text-xs inline-flex items-center gap-1"
                  data-testid={`admin-plan-edit-${eff.slug}`}
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit in Plan Builder
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}




function StoryOfWeekPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [queueData, setQueueData] = useState({ queue: [], available: [] });
  const [queueBusy, setQueueBusy] = useState(false);

  const load = () => api.get("/admin/story-of-the-week/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  const loadQueue = () => api.get("/admin/story-of-the-week/queue").then((r) => setQueueData(r.data)).catch(() => setQueueData({ queue: [], available: [] }));
  useEffect(() => { load(); loadQueue(); }, []);

  const send = async () => {
    if (!window.confirm(
      `Send the featured story to ${(status?.audience?.waitlist || 0) + (status?.audience?.subscribers || 0)} recipients now?`
    )) return;
    setBusy(true);
    try {
      const res = await api.post("/admin/story-of-the-week/send");
      if (res.data?.ok) {
        toast.success(`Sent to ${res.data.sent} recipients${res.data.failed ? ` · ${res.data.failed} failed` : ""}`);
      } else {
        toast.error(res.data?.reason || "Nothing to send");
      }
      await load(); await loadQueue();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const saveQueue = async (newIds) => {
    setQueueBusy(true);
    try {
      await api.post("/admin/story-of-the-week/queue", { story_ids: newIds });
      await loadQueue(); await load();
      toast.success("Rotation updated");
    } catch (e) { toast.error(formatError(e)); }
    finally { setQueueBusy(false); }
  };

  const addToQueue = (id) => {
    const current = queueData.queue.map((s) => s.id);
    if (current.includes(id)) return;
    saveQueue([...current, id]);
  };
  const removeFromQueue = (id) => {
    saveQueue(queueData.queue.filter((s) => s.id !== id).map((s) => s.id));
  };
  const move = (idx, dir) => {
    const ids = queueData.queue.map((s) => s.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    saveQueue(ids);
  };

  if (!status) return null;
  const audience = (status.audience?.waitlist || 0) + (status.audience?.subscribers || 0);
  const queuedIds = new Set(queueData.queue.map((s) => s.id));
  const notQueued = queueData.available.filter((s) => !queuedIds.has(s.id));

  return (
    <div className="card-elevated p-5" data-testid="admin-sotw">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
            <Mail className="w-3 h-3" /> Story of the Week — Email dispatch
          </p>
          <p className="font-display text-xl mt-2">
            {status.featured ? status.featured.brand : "No featured story"}
          </p>
          {status.featured && (
            <p className="text-sm text-muted-foreground mt-1 line-clamp-2 max-w-2xl">"{status.featured.story}"</p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <button onClick={send} disabled={busy || (!status.featured && queueData.queue.length === 0) || audience === 0} className="btn-primary"
            data-testid="admin-sotw-send">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send now"}
          </button>
          {!status.resend_configured && (
            <p className="text-[10px] text-pink-700" data-testid="admin-sotw-mock">
              Resend API key not set — emails will log only.
            </p>
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
        <StatChip label="Waitlist" value={status.audience?.waitlist} />
        <StatChip label="Active subs" value={status.audience?.subscribers} />
        <StatChip label="Last sent" value={status.last_sent_at ? new Date(status.last_sent_at).toLocaleString() : "Never"} />
        <StatChip label="Auto weekly" value="On" hint="Auto-dispatches every 7 days" />
        <StatChip label="Rotation queue" value={`${queueData.queue.length} weeks`} hint="Auto-rotates each dispatch" />
      </div>

      {/* Rotation queue editor */}
      <div className="mt-6 border-t border-shell-border pt-5" data-testid="admin-sotw-queue">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Rotation queue</p>
            <p className="text-sm text-muted-foreground mt-0.5 max-w-lg">
              Queue up the next few weeks of featured stories. Each dispatch (manual or auto) promotes the next one in line and removes it from the queue.
            </p>
          </div>
        </div>

        {queueData.queue.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground italic" data-testid="admin-sotw-queue-empty">Queue is empty. Add approved stories below to schedule them.</p>
        ) : (
          <ol className="mt-4 grid gap-2">
            {queueData.queue.map((s, idx) => (
              <li key={s.id} className="border border-shell-border rounded-xl px-3 py-2 flex items-center gap-3"
                data-testid={`admin-sotw-queue-item-${s.id}`}>
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[11px] font-bold grid place-items-center shrink-0">{idx + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm truncate">{s.brand}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{s.author_name || "—"} · "{s.story?.slice(0, 90)}{s.story?.length > 90 ? "…" : ""}"</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => move(idx, -1)} disabled={idx === 0 || queueBusy}
                    data-testid={`admin-sotw-queue-up-${s.id}`}
                    className="p-1.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-30" title="Move up">
                    <ChevronUpIcon />
                  </button>
                  <button onClick={() => move(idx, 1)} disabled={idx === queueData.queue.length - 1 || queueBusy}
                    data-testid={`admin-sotw-queue-down-${s.id}`}
                    className="p-1.5 rounded hover:bg-muted text-muted-foreground disabled:opacity-30" title="Move down">
                    <ChevronDownIcon />
                  </button>
                  <button onClick={() => removeFromQueue(s.id)} disabled={queueBusy}
                    data-testid={`admin-sotw-queue-remove-${s.id}`}
                    className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                    title="Remove from queue">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ol>
        )}

        {notQueued.length > 0 && (
          <div className="mt-5">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">Add to rotation</p>
            <div className="flex flex-wrap gap-2">
              {notQueued.slice(0, 30).map((s) => (
                <button key={s.id} onClick={() => addToQueue(s.id)} disabled={queueBusy}
                  data-testid={`admin-sotw-queue-add-${s.id}`}
                  className="text-xs border border-shell-border rounded-full px-3 py-1.5 hover:border-primary hover:text-primary transition-colors disabled:opacity-50">
                  + {s.brand}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ChevronUpIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15" /></svg>;
}
function ChevronDownIcon() {
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3.5 h-3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>;
}

function StatChip({ label, value, hint }) {
  return (
    <div className="border border-border rounded-md px-3 py-2">
      <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</p>
      <p className="mt-0.5 font-semibold text-sm">{value ?? "—"}</p>
      {hint && <p className="text-[10px] text-muted-foreground mt-0.5">{hint}</p>}
    </div>
  );
}


// ─────────────────────────────────────────────── Blueprint review queue

function BlueprintQueue() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [score, setScore] = useState(80);
  const [feedback, setFeedback] = useState("");
  const [actionItems, setActionItems] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/admin/blueprints").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const pending = rows.filter((r) => r.status === "submitted");
  const reviewed = rows.filter((r) => r.status === "reviewed");

  const openReview = (row) => {
    setSelected(row);
    setScore(row.review_score ?? 80);
    setFeedback(row.review_feedback ?? "");
    setActionItems(row.review_action_items ?? "");
  };

  const submitReview = async () => {
    if (!selected) return;
    if (feedback.trim().length < 40) {
      toast.error("Please write at least 40 characters of feedback for the founder.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/blueprints/${selected.user_id}/review`, {
        score: Number(score), feedback, action_items: actionItems,
      });
      toast.success("Review sent. The founder has been notified.");
      setSelected(null);
      await load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  if (selected) {
    return (
      <div className="grid lg:grid-cols-[1.35fr_1fr] gap-6" data-testid="admin-blueprint-detail">
        <div className="card-elevated p-6 max-h-[80vh] overflow-y-auto">
          <button onClick={() => setSelected(null)} className="text-xs text-muted-foreground hover:text-primary" data-testid="admin-bp-back">
            ← Back to queue
          </button>
          <p className="mt-3 text-xs uppercase tracking-widest text-primary font-semibold">Submission</p>
          <h2 className="font-display text-2xl">{selected.user_name}</h2>
          <p className="text-sm text-muted-foreground">{selected.user_email}</p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
            Submitted {selected.submitted_at?.slice(0, 10)}
          </p>

          <div className="mt-6 grid gap-4">
            {Object.entries(selected.responses || {}).map(([k, v]) => {
              if (v === "" || v === null || v === undefined || v === false) return null;
              return (
                <div key={k} className="border-t border-border pt-3">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    {k.replace(/_/g, " ")}
                  </p>
                  <p className="text-sm whitespace-pre-line mt-1">{typeof v === "boolean" ? "Yes" : v}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="lg:sticky lg:top-24 h-fit">
          <div className="card-elevated p-6" data-testid="admin-bp-review-form">
            <p className="text-xs uppercase tracking-widest text-primary font-semibold">Panel review</p>
            <h3 className="font-display text-xl mt-1">Score & feedback</h3>

            <label className="block mt-5 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              Score / 100
              <input type="number" min={0} max={100} value={score} onChange={(e) => setScore(e.target.value)}
                data-testid="admin-bp-score"
                className="mt-1 w-full rounded-xl border border-border px-3 py-2 text-lg font-display" />
            </label>

            <label className="block mt-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              Feedback (min 40 chars)
              <textarea rows={7} value={feedback} onChange={(e) => setFeedback(e.target.value)}
                data-testid="admin-bp-feedback"
                className="mt-1 w-full rounded-xl border border-border px-3 py-2 text-sm resize-y" />
            </label>

            <label className="block mt-4 text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              Action items (optional)
              <textarea rows={4} value={actionItems} onChange={(e) => setActionItems(e.target.value)}
                data-testid="admin-bp-actions"
                className="mt-1 w-full rounded-xl border border-border px-3 py-2 text-sm resize-y" />
            </label>

            <button onClick={submitReview} disabled={busy} className="btn-primary mt-5 w-full justify-center"
              data-testid="admin-bp-submit">
              {busy ? "Sending…" : selected.status === "reviewed" ? "Update review" : "Send review & notify founder"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-blueprint-queue">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <StatChip label="Pending review" value={pending.length} hint="Awaiting the panel" />
        <StatChip label="Reviewed" value={reviewed.length} hint="Feedback delivered" />
        <StatChip label="Avg score" value={reviewed.length ? Math.round(reviewed.reduce((s, r) => s + (r.review_score || 0), 0) / reviewed.length) : "—"} hint="Across reviewed" />
      </div>

      {rows.length === 0 ? (
        <div className="card-elevated p-10 text-center text-muted-foreground" data-testid="admin-bp-empty">
          No blueprints have been submitted yet.
        </div>
      ) : (
        <div className="grid gap-3">
          {[...pending, ...reviewed].map((r) => (
            <button key={r.user_id} onClick={() => openReview(r)}
              data-testid={`admin-bp-row-${r.user_id}`}
              className="card-elevated p-4 text-left flex items-center justify-between gap-4 hover:-translate-y-0.5 transition-transform">
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{r.user_name}</p>
                <p className="text-xs text-muted-foreground truncate">{r.user_email}</p>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                  {r.status === "reviewed"
                    ? `Reviewed · Score ${r.review_score}/100 · ${r.reviewed_at?.slice(0, 10)}`
                    : `Submitted ${r.submitted_at?.slice(0, 10)}`}
                </p>
              </div>
              <span className={[
                "text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-bold shrink-0",
                r.status === "reviewed" ? "bg-green-100 text-green-700" : "bg-pink-100 text-pink-700",
              ].join(" ")}>{r.status}</span>
              <span className="text-primary shrink-0">→</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────── Database bulk importer

function DatabaseImporter() {
  const [dbs, setDbs] = useState([]);
  const [slug, setSlug] = useState("");
  const [mode, setMode] = useState("append");
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Approval-workflow state: when a >25k upload is rejected we surface a
  // password field and let the admin retry the same file in one click.
  const [limits, setLimits] = useState(null);           // { standard_limit, extended_limit, recent }
  const [approvalPassword, setApprovalPassword] = useState("");
  const [pendingApproval, setPendingApproval] = useState(null); // { row_count, message }
  const [headerMismatch, setHeaderMismatch] = useState(null);   // { missing, extra, ... }
  const [rowCountEstimate, setRowCountEstimate] = useState(0);
  const [activeJob, setActiveJob] = useState(null);
  const [filterReloadKey, setFilterReloadKey] = useState(0);
  const [filterSetupPrompt, setFilterSetupPrompt] = useState(false);
  useEffect(() => { setFilterSetupPrompt(false); }, [slug]);     // durable-import progress
  // Local-only dismissal ledger: once the admin clicks "Dismiss" on a
  // terminal (done/error) import banner we remember that job.id in
  // localStorage so we never resurrect it on the next mount. Running
  // jobs are always shown regardless (progress must stay visible).
  const DISMISSED_KEY = "cpg_admin_import_dismissed_v1";
  const readDismissed = () => {
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (_e) { return []; }
  };
  const markDismissed = (jobId) => {
    if (!jobId) return;
    try {
      const arr = readDismissed();
      if (!arr.includes(jobId)) {
        // Keep only the last 50 IDs — plenty for a session, prevents
        // unbounded growth for admins who dismiss every import.
        localStorage.setItem(
          DISMISSED_KEY,
          JSON.stringify([jobId, ...arr].slice(0, 50)),
        );
      }
    } catch (_e) { /* noop */ }
  };
  const isDismissed = (jobId) => readDismissed().includes(jobId);
  const LARGE_IMPORT_THRESHOLD = 10000;
  // .xlsx parses server-side with openpyxl (CPU-heavy) so we prefer
  // the durable/async endpoint whenever the file could conceivably
  // hold thousands of rows — file size is the cheapest reliable
  // signal since we can't count xlsx rows client-side without
  // shipping SheetJS. 250 KB comfortably covers ~2k-row workbooks
  // and keeps everything larger off the synchronous 100 s window.
  const LARGE_FILE_SIZE_THRESHOLD = 250 * 1024;

  // ── Resume an in-flight import ────────────────────────────────────
  // Whenever the picked slug changes (including on first mount after a
  // refresh/nav) ask the server if a job is still running for that DB.
  // If so, start polling — the admin lands on the exact progress bar
  // they left, no matter which page they came from.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/databases/${slug}/import-jobs/active`);
        if (cancelled || !data?.job) return;
        // Never resurrect a terminal (done/error) banner the admin has
        // already dismissed — that's the "don't nag me every visit"
        // contract. Running jobs bypass this so live progress is
        // always shown.
        if (data.job.status !== "running" && isDismissed(data.job.id)) return;
        setActiveJob(data.job);
        // Resurrected error jobs: surface the structured header-mismatch
        // panel so the admin sees the exact columns they need to fix
        // even if they left the page before the toast fired.
        if (data.job.status === "error" && data.job.error?.error === "header_mismatch") {
          setHeaderMismatch(data.job.error);
        }
      } catch (_e) { /* ignore — no active job */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Poll while a job is running. Stops as soon as status flips.
  useEffect(() => {
    if (!activeJob || activeJob.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/databases/import-jobs/${activeJob.id}`);
        setActiveJob(data);
        if (data.status === "done") {
          toast.success(importSummary(data.result || {}));
          setFilterSetupPrompt(!!data.result?.filter_setup_required);
          setFilterReloadKey((n) => n + 1);
          api.get("/databases").then((r) => setDbs(r.data)).catch(() => {});
          api.get("/admin/databases/upload-limits").then((r) => setLimits(r.data)).catch(() => {});
        } else if (data.status === "error") {
          if (data.error?.error === "header_mismatch") setHeaderMismatch(data.error);
          else toast.error(data.error?.message || "Import failed");
        }
      } catch (_e) { /* keep polling */ }
    }, 1500);
    return () => clearInterval(t);
  }, [activeJob?.id, activeJob?.status]);


  useEffect(() => {
    api.get("/databases").then((r) => {
      setDbs(r.data);
      if (r.data[0] && !slug) setSlug(r.data[0].slug);
    }).catch(() => {});
    api.get("/admin/databases/upload-limits").then((r) => setLimits(r.data)).catch(() => {});
    // Cross-slug resume: if any DB has a running/recent import, jump
    // straight to that DB so the admin lands on the live progress bar
    // (or completion banner) even after nav / refresh from anywhere.
    api.get("/admin/databases/import-jobs/latest").then((r) => {
      const j = r.data?.job;
      if (!j?.database_slug) return;
      // Skip terminal jobs the admin already dismissed — do not
      // re-open the completion banner on every subsequent visit.
      if (j.status !== "running" && isDismissed(j.id)) return;
      setSlug(j.database_slug);
      setActiveJob(j);
      if (j.status === "error" && j.error?.error === "header_mismatch") {
        setHeaderMismatch(j.error);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFile = (f) => {
    if (!f) return;
    const ok = ["xlsx","xls","csv"].some((ext) => f.name.toLowerCase().endsWith("." + ext));
    if (!ok) { toast.error("Please upload an .xlsx, .xls, or .csv file"); return; }
    setFile(f); setResult(null); setPendingApproval(null); setHeaderMismatch(null);
    // Rough client-side estimate for the confirmation copy — reads only
    // the file size + a sniff to guess whether we're in "large import"
    // territory. Not used for enforcement; server is source of truth.
    if (f.name.toLowerCase().endsWith(".csv")) {
      // ~120 bytes/row heuristic — good enough for the > 10k threshold.
      setRowCountEstimate(Math.round(f.size / 120));
    } else {
      // xlsx: openpyxl-parsed rows aren't linear in file size, so we
      // just skip the estimate and let the confirmation copy fall back
      // to "This is a Replace All operation" for the Replace path.
      setRowCountEstimate(0);
    }
  };

  const upload = async () => {
    if (!file || !slug) { toast.error("Pick a database and a file first"); return; }
    // Replace All is destructive — always confirm.
    if (mode === "replace") {
      const dbName = (dbs.find((d) => d.slug === slug) || {}).name || slug;
      const msg =
        `Replace ALL existing rows in “${dbName}” with the contents of ` +
        `“${file.name}”?\n\n` +
        `The first row of the file will become the new table header. ` +
        `This CAN'T be undone.`;
      if (!window.confirm(msg)) return;
    }
    // Extra confirmation gate for large imports (either mode).
    const isLargeImport =
      rowCountEstimate > LARGE_IMPORT_THRESHOLD ||
      (file && file.size > LARGE_FILE_SIZE_THRESHOLD);
    if (isLargeImport) {
      const sizeHint = rowCountEstimate
        ? `around ${rowCountEstimate.toLocaleString()} rows`
        : `${(file.size / 1024).toFixed(0)} KB`;
      const ok = window.confirm(
        `“${file.name}” looks large (${sizeHint}). It will run in the ` +
        `background — you can leave this page and come back; progress ` +
        `resumes automatically.\n\nContinue?`,
      );
      if (!ok) return;
    }
    setBusy(true); setResult(null); setHeaderMismatch(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      if (approvalPassword) fd.append("approval_password", approvalPassword);
      // Large files → durable async endpoint. The server writes a job
      // doc + spawns a background task; the client polls for progress
      // and the same panel resumes across refreshes / nav.
      if (isLargeImport) {
        const { data } = await api.post(
          `/admin/databases/${slug}/import-async`, fd,
          { headers: { "Content-Type": "multipart/form-data" } });
        setActiveJob({ id: data.job_id, status: "running", database_slug: slug, filename: file.name });
        toast.success("Import started in the background — you can leave this page.");
        setFile(null); setApprovalPassword(""); setPendingApproval(null);
        return;
      }
      const { data } = await api.post(`/admin/databases/${slug}/import`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setFilterSetupPrompt(!!data.filter_setup_required);
      setFilterReloadKey((n) => n + 1);
      const overrideNote = data.override_used ? " (approved override)" : "";
      toast.success(`${importSummary(data)}${overrideNote}`);
      setFile(null); setApprovalPassword(""); setPendingApproval(null);
      api.get("/admin/databases/upload-limits").then((r) => setLimits(r.data)).catch(() => {});
      api.get("/databases").then((r) => setDbs(r.data)).catch(() => {});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 400 && detail?.error === "header_mismatch") {
        // Structured header diff — render inline so the admin sees the
        // exact columns to fix (or switch to Replace All).
        setHeaderMismatch(detail);
        toast.error("File header doesn't match — see details below.");
      } else {
        toast.error(formatError(err));
      }
    }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-db-importer">
      <p className="chip-amber inline-flex items-center gap-1">
        <Upload className="w-3 h-3" /> Bulk database importer
      </p>
      <h2 className="font-display text-3xl mt-3">Add rows to a database</h2>
      <p className="text-sm text-muted-foreground mt-1 max-w-lg">
        Drag in an Excel (.xlsx / .xls) or CSV file. The first row must be column headers.
        Rows with the same email are updated instead of duplicated.
      </p>

      <div className="mt-6 grid gap-4">
        <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
          Target database
          <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="admin-db-select"
            className="mt-1 w-full rounded-xl border border-border px-3 py-2 text-sm bg-white">
            {dbs.map((d) => (
              <option key={d.slug} value={d.slug}>{d.name} ({d.slug})</option>
            ))}
          </select>
        </label>

        {slug && (
          <DbFilterConfigPanel
            key={slug}
            slug={slug}
            reloadKey={filterReloadKey}
            setupPrompt={filterSetupPrompt}
            onSaved={() => setFilterSetupPrompt(false)}
          />
        )}

        <label className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
          Import mode
          <div className="mt-1 flex gap-2">
            {[
              ["append", "Append + upsert (safe)"],
              ["replace", "Replace all existing rows"],
            ].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setMode(v)}
                data-testid={`admin-db-mode-${v}`}
                className={["text-xs rounded-full px-4 py-1.5 border transition-colors",
                  mode === v ? "border-primary bg-primary/10 text-primary font-semibold" : "border-shell-border text-foreground/70"].join(" ")}>
                {l}
              </button>
            ))}
          </div>
        </label>

        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handleFile(e.dataTransfer.files?.[0]); }}
          data-testid="admin-db-dropzone"
          className={["mt-2 rounded-2xl border-2 border-dashed p-8 text-center transition-colors",
            drag ? "border-primary bg-primary/5" : "border-shell-border bg-white"].join(" ")}
        >
          <Upload className="w-8 h-8 text-muted-foreground mx-auto" />
          <p className="mt-3 font-semibold">
            {file ? file.name : "Drop your file here"}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {file ? `${(file.size / 1024).toFixed(1)} KB · ready to import`
                  : "or click to browse. .xlsx, .xls, and .csv supported"}
          </p>
          <input type="file" accept=".xlsx,.xls,.csv"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="admin-db-file"
            className="mt-3 block mx-auto text-xs" />
        </div>

        {/* Row-count policy banner + approval prompt */}
        {limits && limits.recent?.length > 0 && (
          <div className="rounded-xl border border-shell-border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="admin-db-upload-limits">
            <details>
              <summary className="cursor-pointer text-[11px] uppercase tracking-widest font-semibold text-foreground/70">
                Recent uploads · {limits.recent.length}
              </summary>
              <ul className="mt-2 space-y-1">
                {limits.recent.map((r) => (
                  <li key={r.id} className="flex flex-wrap justify-between gap-2">
                    <span className="truncate">
                      <b className="text-foreground">{r.database_slug}</b> · {r.filename || "—"} · {r.row_count?.toLocaleString?.() || r.row_count} rows
                      {r.override_used && <span className="ml-1 text-pink-600 font-semibold">· override</span>}
                    </span>
                    <span className="tabular-nums">{new Date(r.created_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}

        {activeJob && activeJob.status === "running" && (
          <div className="rounded-xl border border-shell-accent/40 bg-shell-accent/5 p-4" data-testid="admin-db-import-progress">
            <div className="flex items-center justify-between gap-3">
              <p className="font-semibold text-sm">Import running — {activeJob.filename}</p>
              <span className="text-[10px] uppercase tracking-widest font-bold text-shell-accent">
                {activeJob.phase || "working"}
              </span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-shell-border overflow-hidden">
              <div
                className="h-full bg-shell-accent transition-all"
                style={{
                  width: activeJob.rows_total
                    ? `${Math.min(100, Math.round((activeJob.rows_read / activeJob.rows_total) * 100))}%`
                    : "10%",
                }}
                data-testid="admin-db-import-progress-bar"
              />
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              {activeJob.rows_total
                ? `${(activeJob.rows_read || 0).toLocaleString()} of ${activeJob.rows_total.toLocaleString()} rows · ${(activeJob.inserted || 0).toLocaleString()} new · ${(activeJob.updated || 0).toLocaleString()} updated`
                : "Preparing your file…"}
            </p>
            <p className="mt-1 text-[10px] text-muted-foreground">
              Safe to leave this page — you can come back and this progress will resume.
            </p>
          </div>
        )}

        {activeJob && activeJob.status === "done" && (
          <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4" data-testid="admin-db-import-done">
            <p className="font-semibold text-sm text-emerald-900">
              Import complete — {activeJob.filename}
            </p>
            <p className="mt-1 text-xs text-emerald-900/80">
              <span data-testid="admin-db-import-done-summary">{importSummary(activeJob.result || {})}</span>
              {" "}· {(activeJob.result?.rows_total || 0).toLocaleString()} rows in file
            </p>
            <button
              className="mt-2 text-[11px] text-emerald-900 underline"
              onClick={() => { markDismissed(activeJob?.id); setActiveJob(null); }}
              data-testid="admin-db-import-done-dismiss"
            >Dismiss</button>
          </div>
        )}

        {activeJob && activeJob.status === "error" && (
          <div className="rounded-xl border border-red-300 bg-red-50 p-4" data-testid="admin-db-import-error">
            <p className="font-semibold text-sm text-red-900">
              Import failed — {activeJob.filename}
            </p>
            <p className="mt-1 text-xs text-red-900/80">
              {activeJob.error?.message || "The import couldn't finish. Check the file and try again."}
            </p>
            <button
              className="mt-2 text-[11px] text-red-900 underline"
              onClick={() => { markDismissed(activeJob?.id); setActiveJob(null); setHeaderMismatch(null); }}
              data-testid="admin-db-import-error-dismiss"
            >Dismiss</button>
          </div>
        )}

        {headerMismatch && (
          <div className="rounded-xl border border-red-300 bg-red-50 p-4" data-testid="admin-db-header-mismatch">
            <p className="font-semibold text-red-900 text-sm">
              File header doesn't match this database
            </p>
            <p className="text-xs text-red-900/80 mt-1">
              {headerMismatch.message || "Edit the file or switch to Replace All to reset the schema."}
            </p>
            {headerMismatch.missing?.length > 0 && (
              <p className="mt-2 text-xs text-red-900">
                <strong>Missing in your file:</strong>{" "}
                <span className="font-mono">{headerMismatch.missing.join(", ")}</span>
              </p>
            )}
            {headerMismatch.extra?.length > 0 && (
              <p className="mt-1 text-xs text-red-900">
                <strong>Extra columns in your file:</strong>{" "}
                <span className="font-mono">{headerMismatch.extra.join(", ")}</span>
              </p>
            )}
            <p className="mt-2 text-[11px] text-red-900/70">
              Tip: Switch mode to <em>Replace All</em> to overwrite the schema with the incoming header.
            </p>
          </div>
        )}

        <button onClick={upload} disabled={busy || !file || !slug}
          data-testid="admin-db-import-submit"
          className="btn-primary justify-center disabled:opacity-60">
          {busy ? "Importing…" : `Import into ${slug || "…"}`}
        </button>
      </div>

      {result && (
        <div className="mt-6 card-elevated p-5" data-testid="admin-db-result">
          <p className="text-xs uppercase tracking-widest text-green-700 font-semibold">Import complete</p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="admin-db-result-stats">
            <StatChip label="Rows in file" value={result.parsed} />
            <StatChip label="Inserted" value={result.inserted} />
            <StatChip label="Updated" value={result.updated} />
            <StatChip label="Unchanged" value={result.unchanged ?? 0} />
            <StatChip label="Duplicates skipped" value={result.skipped_duplicates ?? 0} />
            <StatChip label="Blank skipped" value={result.skipped_blank ?? 0} />
            {result.deleted ? <StatChip label="Removed" value={result.deleted} /> : null}
            <StatChip label="Total in DB" value={result.total} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Industry Directory admin editor ─────────────────────────────────────
function IndustryVendorEditor() {
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | vendor
  const [loading, setLoading] = useState(true);
  const [historyKey, setHistoryKey] = useState(0);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/industry/vendors", { params: { sort: "name", limit: 300 } }).then((r) => setVendors(r.data)),
      api.get("/industry/categories").then((r) => setCategories(r.data)),
    ]).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const togglePin = async (v) => {
    try {
      await api.put(`/admin/industry/vendors/${v.slug}`, {
        ...vendorToPayload(v),
        featured: !v.featured,
      });
      toast.success(v.featured ? "Un-pinned" : "Pinned to the top");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const removeVendor = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"? This also removes all reviews.`)) return;
    try {
      await api.delete(`/admin/industry/vendors/${v.slug}`);
      toast.success("Vendor deleted");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const filtered = vendors.filter((v) => {
    if (categoryFilter !== "all" && v.category !== categoryFilter) return false;
    if (q) {
      const needle = q.toLowerCase();
      if (!(v.name || "").toLowerCase().includes(needle) && !(v.slug || "").includes(needle)) return false;
    }
    return true;
  });

  return (
    <div data-testid="admin-industry-editor">
      <PendingApplicationsCard onChanged={load} />

      <VendorInvitesCard categories={categories} />

      <BulkImportCard onImported={() => { load(); setHistoryKey((n) => n + 1); }} />

      <ImportHistoryCard refreshKey={historyKey} />

      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search vendor name or slug…"
            data-testid="admin-industry-search"
            className="rounded-full border border-border bg-white px-4 py-2 text-sm w-72 focus:outline-none focus:border-primary"
          />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            data-testid="admin-industry-cat-filter"
            className="rounded-full border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          >
            <option value="all">All categories ({vendors.length})</option>
            {categories.map((c) => (
              <option key={c.slug} value={c.slug}>{c.label} ({c.vendor_count})</option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          data-testid="admin-industry-new"
          className="btn-primary text-xs"
        >
          <Plus className="w-3.5 h-3.5" /> New vendor
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading vendors…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-muted-foreground">No vendors match.</p>
      ) : (
        <div className="card-elevated admin-scroll-x">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th className="px-3 py-2">Vendor</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-center">Rating</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => {
                const cat = categories.find((c) => c.slug === v.category);
                return (
                  <tr key={v.slug} data-testid={`admin-industry-row-${v.slug}`} className="border-t border-border">
                    <td className="px-3 py-2 max-w-[320px]">
                      <p className="font-semibold truncate">{v.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{v.slug}</p>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{cat?.label || v.category}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {v.featured && <span className="rounded-full bg-pink-50 text-pink-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">Pinned</span>}
                        {v.verified && <span className="rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">Verified</span>}
                        {v.demo && <span className="rounded-full bg-slate-100 text-slate-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">Demo</span>}
                        {!v.demo && !v.verified && !v.featured && <span className="text-[11px] text-muted-foreground">Live</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {v.member_review_count > 0
                        ? <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 text-pink-500 fill-current" />{v.avg_member_rating?.toFixed?.(1) || 0}<span className="text-muted-foreground text-[11px]"> ({v.member_review_count})</span></span>
                        : <span className="text-[11px] text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => togglePin(v)} data-testid={`admin-industry-pin-${v.slug}`} title={v.featured ? "Un-pin" : "Pin to top"}
                          className={["p-1.5 rounded hover:bg-muted transition", v.featured ? "text-pink-600" : "text-muted-foreground"].join(" ")}>
                          <Pin className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setEditing(v)} data-testid={`admin-industry-edit-${v.slug}`}
                          className="p-1.5 rounded text-shell-side hover:bg-muted transition">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => removeVendor(v)} data-testid={`admin-industry-delete-${v.slug}`}
                          className="p-1.5 rounded text-muted-foreground hover:text-destructive hover:bg-muted transition">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <VendorEditorDrawer
          initial={editing === "new" ? null : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function vendorToPayload(v) {
  return {
    slug: v.slug,
    name: v.name,
    category: v.category,
    subcategory: v.subcategory || "",
    description: v.description || "",
    website: v.website || "",
    location_city: v.location?.city || "",
    location_state: v.location?.state || "",
    location_country: v.location?.country || "",
    services: v.services || [],
    specialties: v.specialties || [],
    certifications: v.certifications || [],
    min_run_size: v.min_run_size || "",
    featured: !!v.featured,
    verified: !!v.verified,
    demo: !!v.demo,
    external_refs: v.external_refs || [],
  };
}

function VendorEditorDrawer({ initial, categories, onClose, onSaved }) {
  const isNew = !initial;
  const [f, setF] = useState(() => (initial ? vendorToPayload(initial) : {
    slug: "", name: "", category: categories[0]?.slug || "co-manufacturers",
    subcategory: "", description: "", website: "",
    location_city: "", location_state: "", location_country: "",
    services: [], specialties: [], certifications: [],
    min_run_size: "", featured: false, verified: false, demo: false,
    external_refs: [],
  }));
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const setRef = (key, patch) => {
    setF((s) => {
      const list = s.external_refs.filter((r) => r.key !== key);
      const existing = s.external_refs.find((r) => r.key === key) || { key };
      const merged = { ...existing, ...patch };
      if (!merged.url && !merged.rating && !merged.note) return { ...s, external_refs: list };
      return { ...s, external_refs: [...list, merged] };
    });
  };

  const submit = async () => {
    if (!f.name.trim() || !f.category) {
      toast.error("Name and category are required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/admin/industry/vendors", f);
        toast.success("Vendor created");
      } else {
        await api.put(`/admin/industry/vendors/${initial.slug}`, f);
        toast.success("Vendor saved");
      }
      onSaved?.();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  const chip = (label, arr, key) => (
    <div>
      <label className="text-xs font-semibold">{label}</label>
      <input
        value={(arr || []).join(", ")}
        onChange={(e) => set(key, e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
        placeholder="Comma-separated"
        className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
        data-testid={`admin-industry-drawer-${key}`}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[9999] flex items-stretch justify-end" role="dialog" aria-modal="true" data-testid="admin-industry-drawer">
      <button aria-label="Close" onClick={onClose} className="absolute inset-0 bg-slate-900/50" />
      <div className="relative w-full max-w-2xl bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-border bg-white px-6 py-4">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary">{isNew ? "New vendor" : "Edit vendor"}</p>
            <p className="font-display text-xl mt-0.5">{f.name || "Untitled"}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name *" value={f.name} onChange={(v) => set("name", v)} testid="admin-industry-drawer-name" />
            <div>
              <label className="text-xs font-semibold">Category *</label>
              <select value={f.category} onChange={(e) => set("category", e.target.value)}
                data-testid="admin-industry-drawer-category"
                className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm">
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
              </select>
            </div>
            {isNew && <Field label="Slug (auto if blank)" value={f.slug} onChange={(v) => set("slug", v)} testid="admin-industry-drawer-slug" />}
            <Field label="Subcategory" value={f.subcategory} onChange={(v) => set("subcategory", v)} testid="admin-industry-drawer-subcategory" />
          </div>
          <Field label="Description" value={f.description} onChange={(v) => set("description", v)} textarea testid="admin-industry-drawer-desc" />
          <Field label="Website" value={f.website} onChange={(v) => set("website", v)} testid="admin-industry-drawer-website" />
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="City" value={f.location_city} onChange={(v) => set("location_city", v)} testid="admin-industry-drawer-city" />
            <Field label="State" value={f.location_state} onChange={(v) => set("location_state", v)} testid="admin-industry-drawer-state" />
            <Field label="Country" value={f.location_country} onChange={(v) => set("location_country", v)} testid="admin-industry-drawer-country" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {chip("Services", f.services, "services")}
            {chip("Specialties", f.specialties, "specialties")}
            {chip("Certifications", f.certifications, "certifications")}
          </div>
          <Field label="Minimum run size" value={f.min_run_size} onChange={(v) => set("min_run_size", v)} testid="admin-industry-drawer-min-run" />

          <div className="grid sm:grid-cols-3 gap-3">
            <Toggle label="Pin to top (featured)" checked={f.featured} onChange={(v) => set("featured", v)} testid="admin-industry-drawer-featured" />
            <Toggle label="Institute-verified" checked={f.verified} onChange={(v) => set("verified", v)} testid="admin-industry-drawer-verified" />
            <Toggle label="Demo placeholder" checked={f.demo} onChange={(v) => set("demo", v)} testid="admin-industry-drawer-demo" />
          </div>

          {/* External references */}
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-shell-side mb-2">External reference overrides</p>
            <p className="text-xs text-muted-foreground mb-3">Leave a source blank to fall back to the pre-filled search URL. Set a URL to send founders to a specific profile, or a rating (0–5) to display the Institute's read.</p>
            <div className="space-y-3">
              {EXTERNAL_REF_SOURCES.map((src) => {
                const cur = (f.external_refs || []).find((r) => r.key === src.key) || {};
                return (
                  <div key={src.key} className="rounded-lg border border-border p-3">
                    <p className="text-sm font-semibold">{src.label}</p>
                    <div className="mt-2 grid sm:grid-cols-[1fr_100px] gap-2">
                      <input
                        value={cur.url || ""}
                        onChange={(e) => setRef(src.key, { url: e.target.value })}
                        placeholder="Custom URL (optional)"
                        data-testid={`admin-industry-drawer-ref-url-${src.key}`}
                        className="w-full rounded-lg border border-border bg-white px-3 py-1.5 text-xs focus:outline-none focus:border-primary"
                      />
                      <input
                        type="number" step="0.1" min="0" max="5"
                        value={typeof cur.rating === "number" ? cur.rating : ""}
                        onChange={(e) => setRef(src.key, { rating: e.target.value === "" ? undefined : Number(e.target.value) })}
                        placeholder="Rating"
                        data-testid={`admin-industry-drawer-ref-rating-${src.key}`}
                        className="w-full rounded-lg border border-border bg-white px-3 py-1.5 text-xs focus:outline-none focus:border-primary"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-border bg-white px-6 py-3 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-xs font-semibold text-muted-foreground px-4 py-2">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="admin-industry-drawer-save" className="btn-primary text-xs">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : isNew ? "Create vendor" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, textarea, testid }) {
  return (
    <div className={textarea ? "" : ""}>
      <label className="text-xs font-semibold">{label}</label>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3}
          data-testid={testid}
          className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm resize-y focus:outline-none focus:border-primary" />
      ) : (
        <input value={value || ""} onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary" />
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        data-testid={testid} className="w-4 h-4 accent-primary" />
      <span className="text-xs font-semibold">{label}</span>
    </label>
  );
}

// Human summary of the shared importer's counters.
function importSummary(r) {
  const parts = [`${r.inserted || 0} new`, `${r.updated || 0} updated`];
  if (r.unchanged) parts.push(`${r.unchanged} unchanged`);
  if (r.skipped_duplicates) parts.push(`${r.skipped_duplicates} duplicate rows skipped`);
  if (r.skipped_blank) parts.push(`${r.skipped_blank} blank rows skipped`);
  if (r.failed) parts.push(`${r.failed} failed`);
  if (r.deleted) parts.push(`${r.deleted} removed`);
  const total = r.total != null ? ` · ${r.total} total records` : "";
  return `Import complete: ${parts.join(" · ")}${total}`;
}

function BulkImportCard({ onImported }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("upsert");
  const [busy, setBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);

  const submit = async (dryRun = false) => {
    if (!file) { toast.error("Choose a .csv file first"); return; }
    if (dryRun) { setPreviewBusy(true); setPreview(null); } else { setBusy(true); setResult(null); }
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      if (dryRun) fd.append("dry_run", "true");
      const res = await fetch(`${API}/admin/industry/vendors/bulk-import`, {
        method: "POST", credentials: "include", body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || (dryRun ? "Preview failed" : "Import failed"));
      if (dryRun) {
        setPreview(data);
        setResult(null);
        const changed = (data.changes || []).filter((c) => !c.unchanged).length;
        toast.success(`Preview: ${data.created} create · ${data.updated} update · ${changed} with changes`);
      } else {
        setResult(data);
        setPreview(null);
        toast.success(`Imported ${data.created + data.updated} · Skipped ${data.skipped}`);
        onImported?.();
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      if (dryRun) setPreviewBusy(false); else setBusy(false);
    }
  };

  const downloadTemplate = () => {
    // Hit the endpoint with credentials, then trigger a download from the blob.
    fetch(`${API}/admin/industry/vendors/csv-template`, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "industry-vendors-template.csv";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Couldn't download the template"));
  };

  return (
    <div className="card-elevated p-5 mb-6" data-testid="admin-industry-bulk-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <FileUp className="w-3 h-3" /> Bulk CSV import
          </p>
          <h3 className="font-display text-xl mt-1">Seed dozens of vendors at once</h3>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">
            Upload a CSV to create or update vendors in one shot. Multi-value columns (services, specialties, certifications) use semicolons. Per-source ratings/URLs go in <code>bbb_url</code>, <code>bbb_rating</code>, etc.
          </p>
        </div>
        <button
          type="button"
          onClick={downloadTemplate}
          data-testid="admin-industry-bulk-template"
          className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-3 py-1.5 text-xs font-semibold hover:border-primary/40 transition"
        >
          <Download className="w-3.5 h-3.5" /> Download template
        </button>
      </div>

      <div className="mt-4 grid md:grid-cols-[1fr_180px_auto_auto] gap-3 items-end">
        <label className="block">
          <span className="text-xs font-semibold">CSV file</span>
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }}
            data-testid="admin-industry-bulk-file"
            className="mt-1 block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-primary/10 file:text-primary file:font-semibold file:px-3 file:py-1.5 file:cursor-pointer"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold">Mode</span>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            data-testid="admin-industry-bulk-mode"
            className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          >
            <option value="upsert">Upsert (create + update)</option>
            <option value="create-only">Create only</option>
            <option value="update-only">Update only</option>
          </select>
        </label>
        <button
          type="button"
          onClick={() => submit(true)}
          disabled={previewBusy || busy || !file}
          data-testid="admin-industry-bulk-preview"
          className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-3 py-2 text-xs font-semibold hover:border-primary/40 transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {previewBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
          {previewBusy ? "Previewing…" : "Preview changes"}
        </button>
        <button
          type="button"
          onClick={() => submit(false)}
          disabled={busy || previewBusy || !file}
          data-testid="admin-industry-bulk-submit"
          className="btn-primary text-xs"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
          {busy ? "Importing…" : "Import CSV"}
        </button>
      </div>

      {result && (
        <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="admin-industry-bulk-result">
          <ResultChip label="Created" value={result.created} tone="emerald" />
          <ResultChip label="Updated" value={result.updated} tone="sky" />
          <ResultChip label="Skipped" value={result.skipped} tone="amber" />
          {result.errors?.length > 0 && (
            <div className="sm:col-span-3 rounded-lg border border-pink-200 bg-pink-50/50 p-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-pink-800 mb-1.5">Rows that were skipped</p>
              <ul className="space-y-0.5 text-[12px] text-pink-900">
                {result.errors.map((e, i) => (
                  <li key={i}><b>Row {e.row}:</b> {e.reason}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {preview && (
        <div className="mt-4" data-testid="admin-industry-bulk-preview-panel">
          <div className="grid sm:grid-cols-3 gap-3 mb-3">
            <ResultChip label="Would create" value={preview.created} tone="emerald" />
            <ResultChip label="Would update" value={preview.updated} tone="sky" />
            <ResultChip label="Would skip" value={preview.skipped} tone="amber" />
          </div>
          {preview.errors?.length > 0 && (
            <div className="rounded-lg border border-pink-200 bg-pink-50/50 p-3 mb-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-pink-800 mb-1.5">Rows that would be skipped</p>
              <ul className="space-y-0.5 text-[12px] text-pink-900">
                {preview.errors.map((e, i) => (
                  <li key={i}><b>Row {e.row}:</b> {e.reason}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary inline-flex items-center gap-1.5">
                <Eye className="w-3 h-3" /> Dry run — nothing has been written
              </p>
              <span className="text-[11px] text-muted-foreground">
                {(preview.changes || []).length} row{(preview.changes || []).length === 1 ? "" : "s"} shown
              </span>
            </div>
            {(preview.changes || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No rows to import from this file.</p>
            ) : (
              <ul className="space-y-3" data-testid="admin-industry-bulk-preview-changes">
                {preview.changes.map((c) => (
                  <li key={`${c.row}-${c.slug}`} className="rounded-lg border border-shell-border bg-white p-3">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${
                          c.action === "create" ? "bg-emerald-100 text-emerald-800" : "bg-sky-100 text-sky-800"
                        }`}>
                          {c.action}
                        </span>
                        <span className="font-display text-base">{c.name}</span>
                        <span className="text-[11px] text-muted-foreground">Row {c.row} · <code className="text-[11px]">{c.slug}</code></span>
                      </div>
                      {c.unchanged && (
                        <span className="text-[11px] text-muted-foreground italic">No fields would change</span>
                      )}
                    </div>
                    {(c.diff || []).length > 0 && (
                      <ul className="space-y-0.5 text-[12px] text-foreground">
                        {c.diff.map((f, i) => (
                          <li key={i} className="grid grid-cols-[110px_1fr] gap-2">
                            <span className="font-semibold text-muted-foreground">{f.field}</span>
                            <span>
                              <span className="text-rose-700 line-through">{f.before}</span>
                              <span className="mx-1.5 text-muted-foreground">→</span>
                              <span className="text-emerald-700 font-semibold">{f.after}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ResultChip({ label, value, tone }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
    amber: "border-pink-200 bg-pink-50 text-pink-800",
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[tone] || ""}`}>
      <p className="text-[10px] font-bold uppercase tracking-[0.14em]">{label}</p>
      <p className="font-display text-2xl mt-0.5">{value}</p>
    </div>
  );
}

function ImportHistoryCard({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [expandedBusy, setExpandedBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/industry/imports")
      .then((r) => setRows(r.data || []))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [refreshKey]);

  const formatWhen = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  };

  const formatSize = (bytes) => {
    if (!bytes) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const downloadOriginal = async (row) => {
    if (!row.file_kept) {
      toast.error("Original file was not retained (file larger than 1 MB).");
      return;
    }
    try {
      const res = await fetch(`${API}/admin/industry/imports/${row.id}/file`, { credentials: "include" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Download failed");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = row.filename || `industry-import-${row.id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const toggleExpand = async (row) => {
    if (expandedId === row.id) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(row.id);
    setExpandedDetail(null);
    if (!row.error_count) return;
    setExpandedBusy(true);
    try {
      const r = await api.get(`/admin/industry/imports/${row.id}`);
      setExpandedDetail(r.data);
    } catch (err) {
      toast.error(formatError(err));
      setExpandedId(null);
    } finally {
      setExpandedBusy(false);
    }
  };

  return (
    <div className="card-elevated p-5 mb-6" data-testid="admin-industry-import-history">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <History className="w-3 h-3" /> Import history
          </p>
          <h3 className="font-display text-xl mt-1">Recent CSV bulk imports</h3>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">
            Audit trail of every bulk vendor upload. Click a row to expand skipped-row reasons, or re-download the original CSV to fix and re-upload.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="admin-industry-import-history-refresh"
          className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-3 py-1.5 text-xs font-semibold hover:border-primary/40 transition disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="admin-industry-import-history-loading">
          Loading…
        </p>
      )}

      {!loading && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="admin-industry-import-history-empty">
          No bulk imports yet. Upload a CSV above to see it here.
        </p>
      )}

      {rows.length > 0 && (
        <div className="admin-scroll-x rounded-xl border border-shell-border">
          <table className="w-full min-w-[1080px] text-sm" data-testid="admin-industry-import-history-table">
            <thead className="bg-shell-muted/60">
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                <th className="px-3 py-2 font-semibold">Uploaded</th>
                <th className="px-3 py-2 font-semibold">File</th>
                <th className="px-3 py-2 font-semibold">Admin</th>
                <th className="px-3 py-2 font-semibold">Mode</th>
                <th className="px-3 py-2 font-semibold text-right">Created</th>
                <th className="px-3 py-2 font-semibold text-right">Updated</th>
                <th className="px-3 py-2 font-semibold text-right">Skipped</th>
                <th className="px-3 py-2 font-semibold text-right">Errors</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isOpen = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      className="border-t border-shell-border hover:bg-shell-muted/40 cursor-pointer"
                      onClick={() => toggleExpand(row)}
                      data-testid={`admin-industry-import-row-${row.id}`}
                    >
                      <td className="px-3 py-2 whitespace-nowrap text-xs">{formatWhen(row.uploaded_at)}</td>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-foreground">{row.filename || "—"}</div>
                        <div className="text-[11px] text-muted-foreground">{formatSize(row.size_bytes)}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{row.admin_email || "—"}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-flex rounded-full border border-shell-border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]">
                          {row.mode || "upsert"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-emerald-700">{row.created ?? 0}</td>
                      <td className="px-3 py-2 text-right font-semibold text-sky-700">{row.updated ?? 0}</td>
                      <td className="px-3 py-2 text-right font-semibold text-pink-700">{row.skipped ?? 0}</td>
                      <td className="px-3 py-2 text-right">
                        {row.error_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-pink-800 font-semibold">
                            <FileWarning className="w-3.5 h-3.5" /> {row.error_count}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); downloadOriginal(row); }}
                          disabled={!row.file_kept}
                          title={row.file_kept ? "Download original CSV" : "Original not retained (file was > 1 MB)"}
                          data-testid={`admin-industry-import-download-${row.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-shell-border px-2.5 py-1 text-[11px] font-semibold hover:border-primary/40 transition disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Download className="w-3 h-3" /> CSV
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-shell-muted/30" data-testid={`admin-industry-import-detail-${row.id}`}>
                        <td colSpan={9} className="px-3 py-3">
                          {expandedBusy && <p className="text-xs text-muted-foreground">Loading skipped-row detail…</p>}
                          {!expandedBusy && row.error_count === 0 && (
                            <p className="text-xs text-emerald-800">
                              No skipped rows — every row was created or updated cleanly.
                            </p>
                          )}
                          {!expandedBusy && expandedDetail && (expandedDetail.errors_preview?.length ?? 0) > 0 && (
                            <div>
                              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-pink-800 mb-1.5">
                                Skipped rows (first 20)
                              </p>
                              <ul className="space-y-0.5 text-[12px] text-pink-900">
                                {expandedDetail.errors_preview.map((e, i) => (
                                  <li key={i}><b>Row {e.row}:</b> {e.reason}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



/* ══════════════════════════════════════════════════════════════════════
   CPG Deals Composer & Broadcast Console
   ══════════════════════════════════════════════════════════════════════ */

const DEAL_KINDS = [
  { value: "deal",     label: "Deal" },
  { value: "discount", label: "Discount" },
  { value: "perk",     label: "Perk" },
  { value: "event",    label: "Event" },
];

const DEAL_KIND_TONE = {
  deal:     "bg-primary/15 text-primary border-primary/30",
  discount: "bg-emerald-100 text-emerald-800 border-emerald-200",
  perk:     "bg-sky-100 text-sky-800 border-sky-200",
  event:    "bg-pink-100 text-pink-900 border-pink-200",
};

function toDateInputValue(iso) {
  if (!iso) return "";
  try { return new Date(iso).toISOString().slice(0, 10); }
  catch { return ""; }
}

function fromDateInputValue(v) {
  if (!v) return null;
  const d = new Date(v + "T23:59:59Z");
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function fmtDealDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

/* ══════════════════════════════════════════════════════════════════════
   Pending Vendor Applications — draft queue admins review + publish
   ══════════════════════════════════════════════════════════════════════ */
function PendingApplicationsCard({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/industry/vendors/pending")
      .then((r) => setRows(r.data || []))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const publish = async (slug) => {
    setBusySlug(slug);
    try {
      await api.post(`/admin/industry/vendors/${slug}/publish`);
      toast.success("Vendor published — now visible in the directory");
      load();
      onChanged?.();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusySlug(null); }
  };

  const reject = async (slug) => {
    if (!window.confirm("Delete this application? This cannot be undone.")) return;
    setBusySlug(slug);
    try {
      await api.delete(`/admin/industry/vendors/${slug}`);
      toast.success("Application removed");
      load();
      onChanged?.();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusySlug(null); }
  };

  if (loading && rows.length === 0) return null;
  if (!loading && rows.length === 0) return null;

  return (
    <div className="card-elevated p-5 mb-6 border-2 border-pink-200/60" data-testid="admin-industry-pending-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-pink-800 inline-flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Awaiting review
          </p>
          <h3 className="font-display text-xl mt-1">Pending vendor applications</h3>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">
            Vendors who submitted their own listing via <code className="text-primary">/vendors/apply</code> — invisible to members until you click Publish.
          </p>
        </div>
        <span className="inline-flex items-center rounded-full bg-pink-100 border border-pink-200 text-pink-900 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em]">
          {rows.length} pending
        </span>
      </div>

      <div className="grid gap-3">
        {rows.map((v) => (
          <div key={v.slug} data-testid={`admin-industry-pending-row-${v.slug}`}
            className="rounded-xl border border-shell-border bg-white p-4 flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="inline-flex rounded-full border border-shell-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  {v.category}
                </span>
                {v.submitted_via === "invite" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary border border-primary/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                    <Link2 className="w-2.5 h-2.5" /> Invited
                  </span>
                )}
              </div>
              <h4 className="font-display text-lg">{v.name}</h4>
              <p className="text-[13px] text-muted-foreground mt-1 line-clamp-2">{v.description}</p>
              <div className="mt-2 text-[11px] text-muted-foreground">
                {v.contact_name && <><b>{v.contact_name}</b> · </>}
                {v.contact_email && <a href={`mailto:${v.contact_email}`} className="text-primary hover:underline">{v.contact_email}</a>}
                {v.contact_phone && <> · {v.contact_phone}</>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <a href={`/industry-directory/${v.slug}`} target="_blank" rel="noopener noreferrer"
                data-testid={`admin-industry-pending-preview-${v.slug}`}
                className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs font-semibold hover:border-primary/40 transition">
                <Eye className="w-3 h-3" /> Preview
              </a>
              <button type="button" onClick={() => publish(v.slug)} disabled={busySlug === v.slug}
                data-testid={`admin-industry-pending-publish-${v.slug}`}
                className="inline-flex items-center gap-1 rounded-full bg-primary text-white px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 transition disabled:opacity-40">
                {busySlug === v.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Publish
              </button>
              <button type="button" onClick={() => reject(v.slug)} disabled={busySlug === v.slug}
                data-testid={`admin-industry-pending-reject-${v.slug}`}
                title="Reject / delete this application"
                className="inline-flex items-center rounded-full border border-shell-border p-1.5 text-muted-foreground hover:text-rose-600 hover:border-rose-200 transition disabled:opacity-40">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Vendor Outreach Invites — admin generates trackable apply links
   ══════════════════════════════════════════════════════════════════════ */
function VendorInvitesCard({ categories }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  const [category, setCategory] = useState("");
  const [invName, setInvName] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invNote, setInvNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/industry/invites")
      .then((r) => setRows(r.data || []))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const inviteUrl = (token) => `${window.location.origin}/vendors/apply?token=${token}`;

  const create = async () => {
    setCreating(true);
    try {
      await api.post("/admin/industry/invites", {
        category: category || null,
        invited_name: invName.trim(),
        invited_email: invEmail.trim(),
        note: invNote.trim(),
      });
      toast.success("Invite link created");
      setCategory(""); setInvName(""); setInvEmail(""); setInvNote("");
      setComposing(false);
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setCreating(false); }
  };

  const remove = async (invite) => {
    if (!window.confirm("Revoke this invite? The link will stop working immediately.")) return;
    try {
      await api.delete(`/admin/industry/invites/${invite.id}`);
      toast.success("Invite revoked");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const copyLink = async (invite) => {
    const url = inviteUrl(invite.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(invite.id);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  const openMail = (invite) => {
    const url = inviteUrl(invite.token);
    const subject = "Get listed in the CPG Institute Vendor Directory";
    const body = [
      invite.invited_name ? `Hi ${invite.invited_name},` : "Hi,",
      "",
      "We'd love to feature you in the CPG Institute Vendor Directory — the searchable list our founder-members use when picking co-manufacturers, ingredient suppliers, and agency partners.",
      "",
      "Use this pre-approved link to submit your listing (takes ~5 minutes):",
      url,
      "",
      "Once submitted, we'll review and publish it. Any questions, hit reply.",
      "",
      "— The CPG Institute team",
    ].join("\n");
    window.location.href = `mailto:${invite.invited_email || ""}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const active = rows.filter((r) => !r.used_at);
  const used = rows.filter((r) => r.used_at);

  return (
    <div className="card-elevated p-5 mb-6" data-testid="admin-industry-invites-card">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <Link2 className="w-3 h-3" /> Vendor outreach
          </p>
          <h3 className="font-display text-xl mt-1">Invite vendors to apply</h3>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">
            Generate a trackable apply link and email it to a vendor. When they use the link, their submission arrives tagged as "Invited" so you know it's high-signal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground">
            <b className="text-foreground">{active.length}</b> active · <b className="text-foreground">{used.length}</b> used
          </span>
          <button type="button" onClick={() => setComposing((v) => !v)}
            data-testid="admin-industry-invite-new"
            className="btn-primary text-xs inline-flex items-center gap-1.5">
            {composing ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            {composing ? "Cancel" : "New invite"}
          </button>
        </div>
      </div>

      {composing && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 mb-4" data-testid="admin-industry-invite-form">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold">Category (optional)</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)}
                data-testid="admin-industry-invite-category"
                className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary">
                <option value="">Any category</option>
                {categories.map((c) => (<option key={c.slug} value={c.slug}>{c.label}</option>))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">Vendor name (optional)</label>
              <input type="text" value={invName} onChange={(e) => setInvName(e.target.value)}
                placeholder="e.g. Acme Co-Pack"
                data-testid="admin-industry-invite-name"
                className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="text-xs font-semibold">Vendor email (optional)</label>
              <input type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)}
                placeholder="jane@acme.example"
                data-testid="admin-industry-invite-email"
                className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary" />
            </div>
          </div>
          <div className="mt-3">
            <label className="text-xs font-semibold">Internal note (optional)</label>
            <input type="text" value={invNote} onChange={(e) => setInvNote(e.target.value)}
              placeholder="e.g. Referred by Sarah at Whole Foods"
              data-testid="admin-industry-invite-note"
              className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary" />
          </div>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={() => setComposing(false)}
              className="text-xs font-semibold text-muted-foreground hover:text-foreground transition">
              Cancel
            </button>
            <button type="button" onClick={create} disabled={creating}
              data-testid="admin-industry-invite-create"
              className="btn-primary text-xs inline-flex items-center gap-1.5">
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
              Generate link
            </button>
          </div>
        </div>
      )}

      {loading && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="admin-industry-invites-loading">Loading…</p>
      )}

      {!loading && rows.length === 0 && !composing && (
        <p className="text-sm text-muted-foreground" data-testid="admin-industry-invites-empty">
          No invites yet. Click <b>New invite</b> to generate your first outreach link.
        </p>
      )}

      {rows.length > 0 && (
        <div className="admin-scroll-x rounded-xl border border-shell-border">
          <table className="w-full min-w-[820px] text-sm" data-testid="admin-industry-invites-table">
            <thead className="bg-shell-muted/50">
              <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                <th className="px-3 py-2 font-semibold">Invited</th>
                <th className="px-3 py-2 font-semibold">Category</th>
                <th className="px-3 py-2 font-semibold">Note</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((inv) => (
                <tr key={inv.id} className="border-t border-shell-border" data-testid={`admin-industry-invite-row-${inv.id}`}>
                  <td className="px-3 py-3">
                    <div className="font-semibold text-foreground">{inv.invited_name || "—"}</div>
                    {inv.invited_email && <div className="text-[11px] text-muted-foreground">{inv.invited_email}</div>}
                  </td>
                  <td className="px-3 py-3 text-xs">{inv.category || <span className="text-muted-foreground italic">Any</span>}</td>
                  <td className="px-3 py-3 text-xs text-muted-foreground max-w-[200px] truncate" title={inv.note}>{inv.note || "—"}</td>
                  <td className="px-3 py-3">
                    {inv.used_at ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                        <CheckCircle2 className="w-2.5 h-2.5" /> Used
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-primary/10 text-primary border border-primary/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right space-x-1">
                    {!inv.used_at && (
                      <>
                        <button type="button" onClick={() => copyLink(inv)}
                          data-testid={`admin-industry-invite-copy-${inv.id}`}
                          className="inline-flex items-center gap-1 rounded-full border border-shell-border px-2.5 py-1 text-[11px] font-semibold hover:border-primary/40 transition"
                          title={inviteUrl(inv.token)}>
                          {copiedId === inv.id ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                          {copiedId === inv.id ? "Copied" : "Copy link"}
                        </button>
                        {inv.invited_email && (
                          <button type="button" onClick={() => openMail(inv)}
                            data-testid={`admin-industry-invite-mail-${inv.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-shell-border px-2.5 py-1 text-[11px] font-semibold hover:border-primary/40 transition"
                            title={`Email ${inv.invited_email}`}>
                            <Mail className="w-3 h-3" /> Email
                          </button>
                        )}
                      </>
                    )}
                    <button type="button" onClick={() => remove(inv)}
                      data-testid={`admin-industry-invite-delete-${inv.id}`}
                      className="inline-flex items-center rounded-full border border-shell-border p-1 text-muted-foreground hover:text-rose-600 hover:border-rose-200 transition"
                      title="Revoke invite">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



function WeeklyDigestPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/digest/status")
      .then((r) => setStatus(r.data || null))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sendNow = async () => {
    if (!window.confirm("Send the weekly digest to every founder right now?")) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/broadcast");
      toast.success(`Digest sent · ${data.in_app_sent}/${data.targeted} in-app delivered`);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSending(false); }
  };

  const ran = status?.ran_at ? new Date(status.ran_at) : null;
  const ranLabel = ran ? ran.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : null;
  const triggeredBy = status?.triggered_by;

  return (
    <div
      data-testid="admin-digest-panel"
      className="rounded-2xl border border-shell-border bg-gradient-to-br from-white to-shell-border/20 p-6 mb-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <History className="w-3 h-3" /> Automated broadcast
          </p>
          <h3 className="font-display text-xl mt-1">Weekly Digest</h3>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl leading-relaxed">
            Fires automatically every <strong>Sunday at 14:00 UTC</strong>. Each founder gets a personalised recap of what they finished in the last 7 days. Only in-app is dispatched; SMS + email respect per-user prefs.
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap text-[12px]">
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-800 px-2.5 py-1"
              data-testid="admin-digest-schedule-chip"
            >
              <Calendar className="w-3 h-3" /> Sundays 14:00 UTC
            </span>
            {loading ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-shell-border/40 px-2.5 py-1 text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> Loading last run…
              </span>
            ) : ran ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-shell-border/40 px-2.5 py-1"
                data-testid="admin-digest-lastrun-chip"
              >
                Last run: <strong>{ranLabel}</strong>
                {status.in_app_sent !== undefined && (
                  <span className="text-muted-foreground">· {status.in_app_sent}/{status.targeted} delivered</span>
                )}
                {triggeredBy && (
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.12em] ${triggeredBy === "cron" ? "bg-emerald-500/15 text-emerald-800" : "bg-primary/15 text-primary"}`}
                  >
                    {triggeredBy === "cron" ? "Auto" : "Manual"}
                  </span>
                )}
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-800 px-2.5 py-1"
                data-testid="admin-digest-lastrun-chip"
              >
                Never run yet
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              data-testid="admin-digest-preview-open"
              className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
            >
              <Eye className="w-3.5 h-3.5" /> Preview
            </button>
            <button
              type="button"
              onClick={sendNow}
              disabled={sending}
              data-testid="admin-digest-send-now"
              className="inline-flex items-center gap-1.5 rounded-full bg-shell-side text-white px-4 py-2 text-xs font-semibold hover:bg-shell-side/90 disabled:opacity-60 transition"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              {sending ? "Sending…" : "Send digest now"}
            </button>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="admin-digest-refresh"
            className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-3 py-1.5 text-[11px] font-semibold hover:border-primary/40 transition disabled:opacity-60"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>
      {previewOpen && <DigestPreviewModal onClose={() => setPreviewOpen(false)} />}
    </div>
  );
}


function DigestPreviewModal({ onClose }) {
  const [users, setUsers] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [q, setQ] = useState("");
  const [preview, setPreview] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/admin/digest/preview/users")
      .then((r) => { if (alive) setUsers(r.data?.users || []); })
      .catch((err) => toast.error(formatError(err)))
      .finally(() => { if (alive) setLoadingUsers(false); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!selectedId) { setPreview(null); return; }
    let alive = true;
    setLoadingPreview(true);
    api.get(`/admin/digest/preview/${selectedId}`)
      .then((r) => { if (alive) setPreview(r.data); })
      .catch((err) => { if (alive) toast.error(formatError(err)); })
      .finally(() => { if (alive) setLoadingPreview(false); });
    return () => { alive = false; };
  }, [selectedId]);

  const filtered = users.filter((u) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (u.email || "").toLowerCase().includes(s) || (u.name || "").toLowerCase().includes(s);
  });

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="admin-digest-preview-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-shell-border">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
              <Eye className="w-3 h-3" /> Digest preview
            </p>
            <h3 className="font-display text-xl mt-1">What this founder will get on Sunday</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-digest-preview-close"
            className="rounded-full p-2 hover:bg-shell-border/40 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid md:grid-cols-[220px,1fr] flex-1 min-h-0">
          {/* User picker */}
          <div className="border-r border-shell-border flex flex-col min-h-0">
            <div className="p-3 border-b border-shell-border">
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search founders…"
                data-testid="admin-digest-preview-search"
                className="w-full rounded-full border border-shell-border px-3 py-1.5 text-xs focus:outline-none focus:border-primary/40"
              />
            </div>
            <div className="flex-1 overflow-auto" data-testid="admin-digest-preview-user-list">
              {loadingUsers && (
                <div className="p-4 text-xs text-muted-foreground inline-flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                </div>
              )}
              {!loadingUsers && filtered.map((u) => (
                <button
                  key={u.user_id}
                  type="button"
                  onClick={() => setSelectedId(u.user_id)}
                  data-testid={`admin-digest-preview-user-${u.user_id}`}
                  className={[
                    "w-full text-left px-3 py-2 border-b border-shell-border/60 transition",
                    selectedId === u.user_id
                      ? "bg-primary/10 border-primary/30"
                      : "hover:bg-shell-border/30",
                  ].join(" ")}
                >
                  <p className="text-xs font-semibold truncate">{u.name || u.email}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>
                  {u.package_slug && (
                    <span className="mt-1 inline-block text-[9px] uppercase tracking-[0.12em] font-bold px-1.5 py-0.5 rounded bg-shell-border/50">
                      {u.package_slug}
                    </span>
                  )}
                </button>
              ))}
              {!loadingUsers && filtered.length === 0 && (
                <p className="p-4 text-xs text-muted-foreground">No matches.</p>
              )}
            </div>
          </div>

          {/* Preview pane */}
          <div className="flex-1 overflow-auto p-6" data-testid="admin-digest-preview-pane">
            {!selectedId && (
              <div className="h-full grid place-items-center text-center">
                <div>
                  <Eye className="w-8 h-8 text-primary/40 mx-auto" />
                  <p className="mt-3 text-sm text-muted-foreground max-w-xs">
                    Pick a founder on the left to see exactly what their digest will look like when Sunday rolls around.
                  </p>
                </div>
              </div>
            )}
            {selectedId && loadingPreview && (
              <div className="text-sm text-muted-foreground inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Building preview…
              </div>
            )}
            {selectedId && !loadingPreview && preview && (
              <div className="space-y-4">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Recipient</p>
                  <p className="font-semibold" data-testid="admin-digest-preview-recipient">
                    {preview.user.name} <span className="text-muted-foreground">({preview.user.email})</span>
                  </p>
                  <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${preview.would_deliver.in_app ? "bg-emerald-500/15 text-emerald-800" : "bg-shell-border/50 text-muted-foreground"}`}>
                      in-app {preview.would_deliver.in_app ? "on" : "off"}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${preview.would_deliver.email ? "bg-emerald-500/15 text-emerald-800" : "bg-shell-border/50 text-muted-foreground"}`}>
                      email {preview.would_deliver.email ? "on" : "off"}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full font-semibold ${preview.would_deliver.sms ? "bg-emerald-500/15 text-emerald-800" : "bg-shell-border/50 text-muted-foreground"}`}>
                      sms {preview.would_deliver.sms ? "on" : "off"}
                    </span>
                  </div>
                </div>
                <div className="rounded-2xl border border-shell-border bg-gradient-to-br from-shell-side to-shell-side/95 text-white p-5">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-shell-accent">Notification title</p>
                  <h4 className="font-display text-lg mt-1" data-testid="admin-digest-preview-title">
                    {preview.headline.title}
                  </h4>
                  <p className="mt-3 text-sm text-white/85 leading-relaxed" data-testid="admin-digest-preview-body">
                    {preview.headline.body}
                  </p>
                </div>
                <div className="rounded-2xl border border-shell-border p-5">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-3">Underlying data</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                    {[
                      { k: "modules", label: "Modules" },
                      { k: "quizzes", label: "Quizzes" },
                      { k: "days_active", label: "Active days" },
                      { k: "referrals", label: "Referrals" },
                    ].map((t) => (
                      <div key={t.k} className="rounded-xl bg-shell-border/20 p-3">
                        <p className="font-display text-2xl leading-none">{preview.digest.totals[t.k]}</p>
                        <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mt-1">{t.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-xs text-muted-foreground space-y-1">
                    <p><strong>Streak:</strong> {preview.digest.streak.current} current · best {preview.digest.streak.best}</p>
                    <p><strong>Blueprint edited this week:</strong> {preview.digest.blueprint.updated_this_week ? "Yes" : "No"} · {preview.digest.blueprint.field_count} fields</p>
                    <p><strong>Unread notifs this week:</strong> {preview.digest.unread_notifications_this_week}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function CronHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/cron/jobs")
      .then((r) => setData(r.data))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const fmtTrigger = (t) => {
    // "cron[day_of_week='sun', hour='14', minute='0']" → "Sundays 14:00"
    if (!t) return "";
    const dow = t.match(/day_of_week='(\w+)'/)?.[1];
    const hour = t.match(/hour='(\d+)'/)?.[1];
    const minute = t.match(/minute='(\d+)'/)?.[1];
    const DOW = { mon: "Mondays", tue: "Tuesdays", wed: "Wednesdays", thu: "Thursdays", fri: "Fridays", sat: "Saturdays", sun: "Sundays" };
    if (dow && hour !== undefined) {
      return `${DOW[dow] || dow} ${String(hour).padStart(2, "0")}:${String(minute || 0).padStart(2, "0")} UTC`;
    }
    return t;
  };

  const fmtNext = (iso) => {
    if (!iso) return "n/a";
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  const fmtLastRun = (last) => {
    if (!last || !last.ran_at) return { text: "Never run", tone: "amber" };
    try {
      const d = new Date(last.ran_at);
      const text = d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
      return { text, tone: last.ok === false ? "rose" : "emerald", triggeredBy: last.triggered_by };
    } catch { return { text: last.ran_at, tone: "emerald" }; }
  };

  return (
    <div
      data-testid="admin-cron-health-card"
      className="rounded-2xl border border-shell-border bg-white p-6 mb-6"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <Clock className="w-3 h-3" /> Cron health
          </p>
          <h3 className="font-display text-xl mt-1">Scheduled jobs</h3>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            Every automated broadcast the platform runs — next fire time, last run, and delivery counts. Missed runs and silent failures show up here first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${data?.scheduler_running ? "bg-emerald-500/15 text-emerald-800 border border-emerald-500/30" : "bg-rose-500/15 text-rose-800 border border-rose-500/30"}`}
            data-testid="admin-cron-scheduler-chip"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${data?.scheduler_running ? "bg-emerald-500" : "bg-rose-500"}`} />
            Scheduler {data?.scheduler_running ? "running" : "stopped"}
          </span>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="admin-cron-refresh"
            className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-3 py-1.5 text-[11px] font-semibold hover:border-primary/40 transition disabled:opacity-60"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {loading && (
        <div className="text-sm text-muted-foreground inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading scheduled jobs…
        </div>
      )}

      {!loading && data?.jobs?.length === 0 && (
        <p className="text-sm text-muted-foreground">No scheduled jobs registered.</p>
      )}

      {!loading && data?.jobs?.length > 0 && (
        <div className="admin-scroll-x rounded-xl border border-shell-border" data-testid="admin-cron-jobs-table">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-shell-border/30 text-left">
              <tr className="[&>th]:px-4 [&>th]:py-2.5 [&>th]:font-semibold [&>th]:uppercase [&>th]:text-[10px] [&>th]:tracking-widest [&>th]:text-muted-foreground">
                <th>Job</th>
                <th>Schedule</th>
                <th>Next run</th>
                <th>Last run</th>
              </tr>
            </thead>
            <tbody>
              {data.jobs.map((job) => {
                const last = fmtLastRun(job.last_run);
                return (
                  <tr
                    key={job.id}
                    data-testid={`admin-cron-job-${job.id}`}
                    className="border-t border-shell-border/60 hover:bg-shell-border/20 transition"
                  >
                    <td className="px-4 py-3 align-top">
                      <p className="font-semibold text-sm">{job.label}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5 max-w-xs">{job.description}</p>
                      <code className="mt-1 inline-block text-[10px] px-1.5 py-0.5 rounded bg-shell-border/40 text-muted-foreground">{job.id}</code>
                    </td>
                    <td className="px-4 py-3 align-top text-[13px]">
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-800 px-2 py-0.5 text-[11px] font-semibold">
                        <Calendar className="w-3 h-3" /> {fmtTrigger(job.trigger)}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-[12px] text-muted-foreground">
                      {fmtNext(job.next_run_at)}
                    </td>
                    <td className="px-4 py-3 align-top text-[12px]">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${last.tone === "emerald" ? "bg-emerald-500/15 text-emerald-800" : last.tone === "rose" ? "bg-rose-500/15 text-rose-800" : "bg-pink-500/15 text-pink-800"}`}
                        >
                          {last.text}
                        </span>
                        {last.triggeredBy && (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.12em] ${last.triggeredBy === "cron" ? "bg-emerald-500/15 text-emerald-800" : "bg-primary/15 text-primary"}`}
                          >
                            {last.triggeredBy === "cron" ? "Auto" : "Manual"}
                          </span>
                        )}
                        {job.last_run && (job.last_run.in_app_sent !== undefined || job.last_run.targeted !== undefined) && (
                          <span className="text-[11px] text-muted-foreground">
                            · {job.last_run.in_app_sent ?? 0}/{job.last_run.targeted ?? 0} delivered
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function AccessCodesConsole() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [qrCode, setQrCode] = useState(null); // { code, tier_label } opens the QR modal
  const [form, setForm] = useState({
    tier: "founder",
    // Duration is admin-facing: a preset key ("30d", "60d", "90d", "6m",
    // "1y", "lifetime", "custom") + optional custom { value, unit }.
    // We convert the choice to `duration_days` (or omit for lifetime)
    // just before POSTing.
    duration_preset: "30d",
    custom_value: 30,
    custom_unit: "days",   // days | months | years
    // Single = 1, Multi = numeric (default 5)
    usage_mode: "single",  // single | multi
    max_uses: 5,
    // How many single-use codes to mint at once. Only surfaces when
    // usage_mode === "single". Values > 1 route to /admin/access-codes/bulk
    // which returns a CSV of every code + pre-filled sign-in link.
    single_count: 1,
    note: "",
    code_expires_at: "",
    custom_code: "",
    email_to: "",          // optional: mail the code to this address
  });

  const [emailing, setEmailing] = useState({});   // per-code send-in-flight
  // Parsed email list from an uploaded CSV/XLSX (single-use mode only).
  const [emailFile, setEmailFile] = useState(null);
  const onEmailFileParsed = (parsed) => {
    setEmailFile(parsed);
    setForm((f) => ({ ...f, single_count: parsed ? parsed.count : 1 }));
  };

  // Convert the admin's duration choice to backend's `duration_days`.
  // Returns null for lifetime.
  const computeDurationDays = () => {
    const preset = form.duration_preset;
    if (preset === "lifetime") return null;
    if (preset === "30d") return 30;
    if (preset === "60d") return 60;
    if (preset === "90d") return 90;
    if (preset === "6m") return 180;
    if (preset === "1y") return 365;
    // custom: value × unit conversion
    const v = parseInt(form.custom_value, 10) || 0;
    if (v <= 0) return null;
    if (form.custom_unit === "years") return v * 365;
    if (form.custom_unit === "months") return v * 30;
    return v; // days
  };

  const load = () => {
    setLoading(true);
    api.get("/admin/access-codes")
      .then((r) => setRows(r.data || []))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const createCode = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const dur = computeDurationDays();
      const codeExpiresAt = form.code_expires_at
        ? new Date(form.code_expires_at).toISOString()
        : null;
      const noteTrimmed = form.note?.trim() || "";
      const emailTo = form.email_to.trim();
      const useEmailList = form.usage_mode === "single" && !!emailFile;
      const bulkCount = useEmailList
        ? emailFile.count
        : form.usage_mode === "single"
          ? Math.max(1, parseInt(form.single_count, 10) || 1)
          : 1;

      // ── Bulk single-use path: N > 1 mints in one call and downloads
      //    a CSV of every code + its pre-filled sign-in link. Email (if
      //    provided) receives the same CSV as an attachment.
      if (useEmailList || (form.usage_mode === "single" && bulkCount > 1)) {
        const payload = {
          tier: form.tier,
          count: bulkCount,
          note: noteTrimmed,
        };
        if (useEmailList) payload.emails = emailFile.emails;
        if (dur) payload.duration_days = dur;
        if (codeExpiresAt) payload.code_expires_at = codeExpiresAt;
        if (emailTo) payload.email_to = emailTo;
        const { data } = await api.post("/admin/access-codes/bulk", payload);
        // Trigger an immediate XLSX download for the admin. Backend
        // returns `xlsx_base64` + `xlsx_filename`; older responses used
        // `csv_*` keys pointing at the same bytes, so we fall back for
        // any in-flight admin tab that hasn't refreshed yet.
        try {
          const b64 = data.xlsx_base64 || data.csv_base64;
          const name = data.xlsx_filename || data.csv_filename || "access-codes.xlsx";
          const bin = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = name;
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } catch (dlErr) { /* download best-effort */ }
        toast.success(useEmailList
          ? `Generated ${data.count} email-locked codes — Excel file downloaded`
          : `Generated ${data.count} single-use codes — Excel file downloaded`);
        if (emailTo) {
          if (data.email?.ok) toast.success(`Emailed the Excel file → ${emailTo}`);
          else toast.error(`Codes created but email failed: ${data.email?.error || "unknown error"}`);
        }
      } else {
        // ── Original single / multi-use path stays unchanged.
        const payload = {
          tier: form.tier,
          max_uses: form.usage_mode === "single" ? 1 : (parseInt(form.max_uses, 10) || 5),
          note: noteTrimmed,
        };
        if (dur) payload.duration_days = dur;
        if (codeExpiresAt) payload.code_expires_at = codeExpiresAt;
        if (form.custom_code?.trim()) payload.custom_code = form.custom_code.trim();
        const { data } = await api.post("/admin/access-codes", payload);
        toast.success(`Code ${data.code} created`);

        // If an email address is set, immediately mail the code + a
        // pre-filled sign-in link. Failures don't roll back creation —
        // admin can retry the send from the row action later.
        if (emailTo) {
          try {
            await api.post(`/admin/access-codes/${data.code}/send-email`, { email: emailTo });
            toast.success(`Emailed ${data.code} → ${emailTo}`);
          } catch (err) {
            toast.error(`Code created but email failed: ${formatError(err)}`);
          }
        }
      }

      setForm({
        tier: "founder", duration_preset: "30d", custom_value: 30, custom_unit: "days",
        usage_mode: "single", max_uses: 5, single_count: 1, note: "", code_expires_at: "",
        custom_code: "", email_to: "",
      });
      setEmailFile(null);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setCreating(false); }
  };

  // Send an existing code from the table row. Prompts for a recipient
  // email inline (single-click flow: admin types email in prompt,
  // backend mails, toast confirms).
  const sendCodeEmail = async (code) => {
    const to = window.prompt(`Send access code ${code} to which email?`);
    if (!to) return;
    setEmailing((m) => ({ ...m, [code]: true }));
    try {
      const { data } = await api.post(`/admin/access-codes/${code}/send-email`, { email: to.trim() });
      toast.success(data?.mocked ? `Queued (mock) → ${to}` : `Emailed → ${to}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setEmailing((m) => { const next = { ...m }; delete next[code]; return next; });
    }
  };

  const revoke = async (code) => {
    if (!window.confirm(`Revoke ${code}? Anyone who already redeemed it keeps their access.`)) return;
    try {
      await api.post(`/admin/access-codes/${code}/revoke`);
      toast.success(`${code} revoked`);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const remove = async (code) => {
    if (!window.confirm(`Delete ${code} permanently? The audit log is purged too.`)) return;
    try {
      await api.delete(`/admin/access-codes/${code}`);
      toast.success(`${code} deleted`);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const copyRedeemLink = async (code, assignedEmail) => {
    const url = assignedEmail
      ? `${window.location.origin}/access-code-login?code=${code}&email=${encodeURIComponent(assignedEmail)}`
      : `${window.location.origin}/redeem?code=${code}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Redeem link copied");
    } catch { toast.error("Could not copy link"); }
  };

  const tierPill = (tier) => {
    const map = {
      free:    "bg-shell-border/50 text-muted-foreground",
      founder: "bg-sky-500/15 text-sky-800 border-sky-500/30",
      pro:     "bg-primary/15 text-primary border-primary/30",
      // Legacy alias colors — keep so historical codes still render tidy pills.
      starter: "bg-sky-500/15 text-sky-800 border-sky-500/30",
      growth:  "bg-primary/15 text-primary border-primary/30",
      full:    "bg-emerald-500/15 text-emerald-800 border-emerald-500/30",
      student: "bg-pink-500/15 text-pink-800 border-pink-500/30",
    };
    return (
      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] border ${map[tier] || "bg-shell-border/50"}`}>
        {tier}
      </span>
    );
  };

  return (
    <div data-testid="admin-access-codes-console">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <Gift className="w-3 h-3" /> Comp access
          </p>
          <h2 className="font-display text-2xl mt-1">Access Codes</h2>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            Mint invite codes that unlock <strong>Founder Membership</strong> or <strong>Founder Pro</strong> — permanently or for a set number of days. Each code ships with a QR code you can print, drop into decks, or DM.
          </p>
        </div>
      </div>

      {/* Composer */}
      <form
        onSubmit={createCode}
        data-testid="admin-access-code-form"
        className="rounded-2xl border border-shell-border bg-white p-5 mb-6"
      >
        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-4">New code</p>
        <div className="grid md:grid-cols-3 gap-3">
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em]">
            Tier
            <select
              value={form.tier}
              onChange={(e) => setForm({ ...form, tier: e.target.value })}
              data-testid="access-code-tier"
              className="mt-1 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
            >
              <option value="founder">Founder Membership</option>
              <option value="pro">Founder Pro Membership</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em]">
            Code Expiry Duration
            <select
              value={form.duration_preset}
              onChange={(e) => setForm({ ...form, duration_preset: e.target.value })}
              data-testid="access-code-duration-preset"
              className="mt-1 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
            >
              <option value="30d">30 Days</option>
              <option value="60d">60 Days</option>
              <option value="90d">90 Days</option>
              <option value="6m">6 Months</option>
              <option value="1y">1 Year</option>
              <option value="lifetime">Lifetime</option>
              <option value="custom">Custom…</option>
            </select>
          </label>
          {form.duration_preset === "custom" && (
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em]">
              Custom duration
              <div className="mt-1 flex gap-2">
                <input
                  type="number" min="1" max="3650"
                  value={form.custom_value}
                  onChange={(e) => setForm({ ...form, custom_value: e.target.value })}
                  data-testid="access-code-duration-custom-value"
                  className="w-1/2 rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
                />
                <select
                  value={form.custom_unit}
                  onChange={(e) => setForm({ ...form, custom_unit: e.target.value })}
                  data-testid="access-code-duration-custom-unit"
                  className="w-1/2 rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
                >
                  <option value="days">Days</option>
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>
            </label>
          )}
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em]">
            Max uses
            <select
              value={form.usage_mode}
              onChange={(e) => setForm({ ...form, usage_mode: e.target.value })}
              data-testid="access-code-usage-mode"
              className="mt-1 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
            >
              <option value="single">Single (1 use only)</option>
              <option value="multi">Multi-use</option>
            </select>
          </label>
          {form.usage_mode === "single" && (
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em]">
              Count
              <input
                type="number" min="1" max="500"
                value={form.single_count}
                onChange={(e) => setForm({ ...form, single_count: e.target.value })}
                readOnly={!!emailFile}
                data-testid="access-code-single-count"
                placeholder="1"
                className={`mt-1 w-full rounded-lg border border-shell-border px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40 ${emailFile ? "bg-muted/50 cursor-not-allowed" : "bg-white"}`}
              />
              <span className="mt-1 block text-[10px] normal-case tracking-normal text-muted-foreground">
                {emailFile
                  ? "Set automatically from the valid, unique emails in your file — one code per email."
                  : "Keep at 1 to email a single code. Set higher to mint a batch of single-use codes and download an Excel (.xlsx) file with every code + pre-filled sign-in link."}
              </span>
            </label>
          )}
          {form.usage_mode === "single" && (
            <AccessCodeEmailUpload parsed={emailFile} onParsed={onEmailFileParsed} />
          )}
          {form.usage_mode === "multi" && (
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em]">
              Multi-use limit
              <input
                type="number" min="2" max="10000"
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                data-testid="access-code-max-uses"
                placeholder="e.g. 25"
                className="mt-1 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
              />
            </label>
          )}
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em] md:col-span-2">
            Note (internal)
            <input
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              data-testid="access-code-note"
              placeholder="Who's it for? e.g. Jane at Aisle Nine"
              className="mt-1 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
            />
          </label>
          <label className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em] md:col-span-2">
            Send email to (optional)
            <input
              type="email"
              value={form.email_to}
              onChange={(e) => setForm({ ...form, email_to: e.target.value })}
              data-testid="access-code-email-to"
              placeholder="founder@example.com — sends the code + a pre-filled sign-in link"
              className="mt-1 w-full rounded-lg border border-shell-border bg-white px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary/40"
            />
            {form.usage_mode === "single" && emailFile && (
              <span className="mt-1 block text-[11px] normal-case tracking-normal font-semibold text-rose-700" data-testid="access-code-email-to-note">
                Note: only this address receives an email — one message with the attachment containing every generated code (Email Address, Access Code, Sign-In Link). The people in your uploaded file are not emailed individually.
              </span>
            )}
          </label>
          {/*
            "Code expires (optional)" date field removed — the
            `duration_preset` above ("30 Days", "60 Days" … "Lifetime")
            already controls access-code expiry, so the second field
            was creating two competing sources of truth. The form
            state `code_expires_at` is intentionally kept so the
            payload builder at the top of this component stays a
            no-op instead of throwing on undefined.
          */}
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <button
            type="submit"
            disabled={creating}
            data-testid="access-code-create-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-5 py-2.5 text-xs font-bold hover:bg-primary/90 disabled:opacity-60 transition"
          >
            {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            {creating ? "Generating…" : "Generate code"}
          </button>
          <p className="text-[11px] text-muted-foreground">
            A short, human-readable code will be generated automatically (e.g. <code>CPG-FULL-K7XR-B2N9</code>).
          </p>
        </div>
      </form>

      {/* List */}
      {loading && (
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading codes…
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-shell-border bg-white p-8 text-center" data-testid="admin-access-codes-empty">
          <Gift className="w-8 h-8 mx-auto text-primary/40" />
          <p className="mt-3 font-display text-lg">No codes yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Fill in the form above to mint your first invite.</p>
        </div>
      )}
      {!loading && rows.length > 0 && (
        <div className="admin-scroll-x rounded-2xl border border-shell-border bg-white" data-testid="admin-access-codes-table">
          <table className="w-full min-w-[1240px] text-sm">
            <thead className="bg-shell-border/30 text-left">
              <tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:uppercase [&>th]:text-[10px] [&>th]:tracking-widest [&>th]:text-muted-foreground">
                <th>Code</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Duration</th>
                <th>Usage</th>
                <th>Note</th>
                <th title="Active = code can still be redeemed by a new member. Used up = every single-use redemption slot has been claimed (used_count reached max_uses) — nobody new can redeem, existing members keep access. Revoked = admin manually disabled the code — existing members keep access but no new redemptions are allowed.">
                  <span className="inline-flex items-center gap-1">
                    Status
                    <span
                      aria-label="Active, Used up, or Revoked. Hover for full explanation."
                      className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-muted text-[10px] font-bold text-muted-foreground cursor-help"
                    >?</span>
                  </span>
                </th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const usage = `${r.used_count || 0}/${r.max_uses || "∞"}`;
                const revoked = r.status === "revoked";
                const exhausted = r.max_uses && (r.used_count >= r.max_uses);
                return (
                  <tr key={r.code} className="border-t border-shell-border/60 hover:bg-shell-border/20 transition" data-testid={`access-code-row-${r.code}`}>
                    <td className="px-4 py-3">
                      <code className="font-mono text-[13px] font-semibold text-foreground">{r.code}</code>
                    </td>
                    <td className="px-4 py-3 align-middle text-[12px] max-w-[240px] truncate" title={r.email || ""} data-testid={`access-code-email-${r.code}`}>
                      {r.email || <span className="text-muted-foreground/60">-</span>}
                    </td>
                    <td className="px-4 py-3 align-middle">{tierPill(r.tier)}</td>
                    <td className="px-4 py-3 align-middle text-[12px]">
                      {r.duration_days ? `${r.duration_days} days` : <span className="text-emerald-700 font-semibold">Lifetime</span>}
                    </td>
                    <td className="px-4 py-3 align-middle text-[12px] font-semibold">{usage}</td>
                    <td className="px-4 py-3 align-middle text-[12px] text-muted-foreground max-w-[220px] truncate" title={r.note}>
                      {r.note || <span className="italic text-muted-foreground/60">—</span>}
                    </td>
                    <td className="px-4 py-3 align-middle">
                      {revoked ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 text-rose-800 border border-rose-500/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]">
                          Revoked
                        </span>
                      ) : exhausted ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-pink-500/15 text-pink-800 border border-pink-500/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]">
                          Used up
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-800 border border-emerald-500/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em]">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <div className="flex items-center gap-1.5 justify-end whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => setQrCode({ code: r.code, tier_label: r.tier })}
                          data-testid={`access-code-qr-${r.code}`}
                          className="inline-flex items-center gap-1 rounded-full border border-shell-border px-2.5 py-1 text-[11px] font-semibold hover:border-primary/40 transition whitespace-nowrap"
                          title="Show QR code"
                        >
                          <QrCode className="w-3 h-3" /> QR
                        </button>
                        <button
                          type="button"
                          onClick={() => copyRedeemLink(r.code, r.assigned_email)}
                          data-testid={`access-code-copy-${r.code}`}
                          className="inline-flex items-center gap-1 rounded-full border border-shell-border px-2.5 py-1 text-[11px] font-semibold hover:border-primary/40 transition whitespace-nowrap"
                          title="Copy redeem link"
                        >
                          <Copy className="w-3 h-3" /> Copy link
                        </button>
                        {!revoked && (
                          <button
                            type="button"
                            onClick={() => revoke(r.code)}
                            data-testid={`access-code-revoke-${r.code}`}
                            className="inline-flex items-center gap-1 rounded-full border border-pink-500/30 text-pink-800 px-2.5 py-1 text-[11px] font-semibold hover:bg-pink-500/10 transition whitespace-nowrap"
                          >
                            <Ban className="w-3 h-3" /> Revoke
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => remove(r.code)}
                          data-testid={`access-code-delete-${r.code}`}
                          className="inline-flex items-center gap-1 rounded-full border border-rose-500/30 text-rose-800 px-2.5 py-1 text-[11px] font-semibold hover:bg-rose-500/10 transition"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {qrCode && <AccessCodeQrModal code={qrCode.code} onClose={() => setQrCode(null)} />}
    </div>
  );
}


function AccessCodeEmailUpload({ parsed, onParsed }) {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(csv|xlsx)$/i.test(file.name)) {
      toast.error("Unsupported file type. Upload a .csv or .xlsx file.");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/access-codes/parse-emails", fd);
      onParsed(data);
      toast.success(`${data.count} valid email${data.count === 1 ? "" : "s"} found`);
    } catch (err) {
      onParsed(null);
      toast.error(formatError(err));
      e.target.value = "";
    } finally {
      setBusy(false);
    }
  };
  const clear = () => { onParsed(null); if (inputRef.current) inputRef.current.value = ""; };
  return (
    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-[0.12em] md:col-span-2" data-testid="access-code-email-upload">
      Email list (optional, CSV or XLSX)
      <div className="mt-1 flex items-center gap-2 flex-wrap normal-case tracking-normal">
        <input ref={inputRef} type="file" accept=".csv,.xlsx" onChange={onFile} disabled={busy}
          data-testid="access-code-email-file-input"
          className="text-sm text-foreground/80 file:mr-3 file:rounded-full file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-xs file:font-semibold file:text-secondary-foreground file:cursor-pointer hover:file:bg-secondary/85" />
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {parsed && (
          <button type="button" onClick={clear} data-testid="access-code-email-file-clear"
            className="inline-flex items-center gap-1 rounded-full border border-shell-border px-2.5 py-1 text-[11px] font-semibold text-foreground hover:border-primary/40">
            <X className="w-3 h-3" /> Remove file
          </button>
        )}
      </div>
      {parsed ? (
        <div className="mt-2 text-[11px] normal-case tracking-normal font-normal text-foreground/80 space-y-0.5" data-testid="access-code-email-file-summary">
          <p><strong>{parsed.filename}</strong>: <span className="text-emerald-700 font-semibold">{parsed.count} valid, unique</span>
            {" · "}{parsed.duplicate_count} duplicate{parsed.duplicate_count === 1 ? "" : "s"} skipped
            {" · "}{parsed.invalid_count} invalid skipped</p>
          {parsed.invalid_count > 0 && (
            <p className="text-rose-700 truncate" title={parsed.invalid.join(", ")} data-testid="access-code-email-file-invalid">
              Invalid: {parsed.invalid.slice(0, 5).join(", ")}{parsed.invalid_count > 5 ? ` and ${parsed.invalid_count - 5} more` : ""}
            </p>
          )}
          <p className="text-muted-foreground">One code is generated per email and only works with that email.</p>
        </div>
      ) : (
        <span className="mt-1 block text-[10px] normal-case tracking-normal font-normal text-muted-foreground">
          Upload a list of emails to generate one single-use code per email, locked to that address. Up to 500 emails, 2 MB.
        </span>
      )}
    </div>
  );
}


function AccessCodeQrModal({ code, onClose }) {
  const redeemUrl = `${window.location.origin}/redeem?code=${code}`;
  const qrUrl = `${API}/admin/access-codes/${code}/qr`;
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(redeemUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch { toast.error("Could not copy link"); }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="access-code-qr-modal"
      onClick={onClose}
    >
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary inline-flex items-center gap-1.5">
              <QrCode className="w-3 h-3" /> Scan or share
            </p>
            <h3 className="font-display text-xl mt-1">Redeem QR</h3>
          </div>
          <button type="button" onClick={onClose} data-testid="access-code-qr-close" className="rounded-full p-2 hover:bg-shell-border/40 transition">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center justify-center rounded-2xl bg-shell-border/20 p-4">
          <img
            src={qrUrl}
            alt={`QR code for ${code}`}
            className="w-64 h-64 object-contain"
            data-testid="access-code-qr-image"
          />
        </div>
        <p className="mt-4 font-mono text-sm text-center break-all" data-testid="access-code-qr-code-text">{code}</p>
        <p className="mt-1 text-[11px] text-center text-muted-foreground break-all">{redeemUrl}</p>
        <div className="mt-5 flex items-center gap-2 justify-center flex-wrap">
          <button
            type="button"
            onClick={copyLink}
            data-testid="access-code-qr-copy"
            className="inline-flex items-center gap-1.5 rounded-full bg-shell-side text-white px-4 py-2 text-xs font-semibold hover:bg-shell-side/90 transition"
          >
            {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? "Copied" : "Copy link"}
          </button>
          <a
            href={qrUrl}
            download={`${code}.png`}
            data-testid="access-code-qr-download"
            className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
          >
            <Download className="w-3.5 h-3.5" /> Download PNG
          </a>
        </div>
      </div>
    </div>
  );
}


function StudentApplicationsConsole() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [pendingCount, setPendingCount] = useState(0);
  const [selected, setSelected] = useState(null); // active application in the reviewer

  const load = () => {
    setLoading(true);
    const query = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/students${query}`)
      .then((r) => { setRows(r.data?.applications || []); setPendingCount(r.data?.pending_count || 0); })
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [filter]);

  const act = async (application, action, reason = "") => {
    if (action === "reject" && !reason) {
      reason = window.prompt("Why are you declining? (Sent to the applicant)");
      if (reason === null) return;
    }
    try {
      const { data } = await api.post(`/admin/students/${application.id}/${action}`, { reason });
      toast.success(`Application ${data.status}`);
      setSelected(null);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const remove = async (application) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await api.delete(`/admin/students/${application.id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const statusPill = (s) => {
    const map = {
      pending:  "bg-pink-500/15 text-pink-800 border-pink-500/30",
      approved: "bg-emerald-500/15 text-emerald-800 border-emerald-500/30",
      rejected: "bg-rose-500/15 text-rose-800 border-rose-500/30",
    };
    return (
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] border ${map[s] || "bg-shell-border/50"}`}>
        {s}
      </span>
    );
  };

  return (
    <div data-testid="admin-students-console">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <GraduationCap className="w-3 h-3" /> Student verification
          </p>
          <h2 className="font-display text-2xl mt-1">Student Applications</h2>
          <p className="mt-1 text-sm text-muted-foreground max-w-xl">
            Review student ID uploads and verification videos. Approved applicants get Full Access for 365 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-pink-500/15 text-pink-800 border border-pink-500/30 px-2.5 py-1 text-[11px] font-semibold" data-testid="admin-students-pending-count">
              {pendingCount} pending
            </span>
          )}
          <button type="button" onClick={load} disabled={loading} data-testid="admin-students-refresh" className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-3 py-1.5 text-[11px] font-semibold hover:border-primary/40 transition">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="admin-students-filter-row">
        {["pending", "approved", "rejected", "all"].map((f) => (
          <button
            key={f} type="button" onClick={() => setFilter(f)}
            data-testid={`admin-students-filter-${f}`}
            className={[
              "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition border",
              filter === f ? "bg-shell-side text-white border-shell-side" : "bg-white text-foreground border-shell-border hover:border-primary/40",
            ].join(" ")}
          >
            {f[0].toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {loading && (
        <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading applications…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-shell-border bg-white p-8 text-center" data-testid="admin-students-empty">
          <GraduationCap className="w-8 h-8 mx-auto text-primary/40" />
          <p className="mt-3 font-display text-lg">No {filter === "all" ? "" : filter} applications</p>
          <p className="mt-1 text-sm text-muted-foreground">Students apply at <code>/apply/student</code>.</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="admin-scroll-x rounded-2xl border border-shell-border bg-white" data-testid="admin-students-table">
          <table className="w-full min-w-[820px] text-sm">
            <thead className="bg-shell-border/30 text-left">
              <tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:uppercase [&>th]:text-[10px] [&>th]:tracking-widest [&>th]:text-muted-foreground">
                <th>Applicant</th>
                <th>School</th>
                <th>Submitted</th>
                <th>Status</th>
                <th className="text-right">Review</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-shell-border/60 hover:bg-shell-border/20 transition" data-testid={`admin-student-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold">{r.name}</p>
                    <p className="text-[11px] text-muted-foreground">{r.email}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium">{r.school}</p>
                    {r.program && <p className="text-[11px] text-muted-foreground">{r.program}{r.graduation_year ? ` · ${r.graduation_year}` : ""}</p>}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-muted-foreground">{new Date(r.submitted_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">{statusPill(r.status)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button" onClick={() => setSelected(r)}
                      data-testid={`admin-student-review-${r.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-shell-border bg-white px-3 py-1.5 text-[11px] font-semibold hover:border-primary/40 transition"
                    >
                      <Eye className="w-3 h-3" /> Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <StudentReviewModal
          application={selected}
          onClose={() => setSelected(null)}
          onApprove={() => act(selected, "approve")}
          onReject={() => act(selected, "reject")}
          onDelete={() => remove(selected)}
          statusPill={statusPill}
        />
      )}
    </div>
  );
}


function StudentReviewModal({ application, onClose, onApprove, onReject, onDelete, statusPill }) {
  const app = application;
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="admin-student-review-modal"
      onClick={onClose}
    >
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-shell-border">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-primary inline-flex items-center gap-1.5">
              <GraduationCap className="w-3 h-3" /> Review application
            </p>
            <h3 className="font-display text-xl mt-1">{app.name}</h3>
            <p className="text-[11px] text-muted-foreground">{app.email}</p>
          </div>
          <div className="flex items-center gap-2">
            {statusPill(app.status)}
            <button type="button" onClick={onClose} data-testid="admin-student-modal-close" className="rounded-full p-2 hover:bg-shell-border/40 transition">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6 grid md:grid-cols-2 gap-5">
          <div className="space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">School</p>
              <p className="mt-1 font-semibold">{app.school}</p>
              {(app.program || app.graduation_year) && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {app.program}{app.program && app.graduation_year ? " · " : ""}{app.graduation_year || ""}
                </p>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Student ID</p>
              <a href={app.id_upload_url} target="_blank" rel="noopener noreferrer" className="block mt-2" data-testid="admin-student-id-link">
                <img
                  src={app.id_upload_url}
                  alt="Student ID"
                  className="w-full rounded-xl border border-shell-border max-h-72 object-contain bg-shell-border/20"
                />
              </a>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Verification video</p>
              <video
                src={app.video_upload_url}
                controls
                className="mt-2 w-full rounded-xl border border-shell-border bg-black"
                data-testid="admin-student-video"
              />
            </div>
            <div className="text-xs text-muted-foreground">
              <p>Submitted {new Date(app.submitted_at).toLocaleString()}</p>
              {app.reviewed_at && <p>Reviewed {new Date(app.reviewed_at).toLocaleString()} by {app.reviewed_by_email}</p>}
              {app.comp_expires_at && <p>Access until {new Date(app.comp_expires_at).toLocaleDateString()}</p>}
              {app.rejection_reason && <p className="mt-2 italic">Reason sent: “{app.rejection_reason}”</p>}
            </div>
          </div>
        </div>

        <div className="border-t border-shell-border p-4 flex items-center gap-2 justify-end flex-wrap">
          <button type="button" onClick={onDelete} data-testid="admin-student-delete" className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/30 text-rose-800 px-3 py-2 text-xs font-semibold hover:bg-rose-500/10 transition">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
          {(app.status === "pending" || app.status === "approved") && (
            <button type="button" onClick={onReject} data-testid="admin-student-reject" className="inline-flex items-center gap-1.5 rounded-full border border-pink-500/30 text-pink-800 px-4 py-2 text-xs font-semibold hover:bg-pink-500/10 transition">
              <Ban className="w-3.5 h-3.5" /> Decline
            </button>
          )}
          {(app.status === "pending" || app.status === "rejected") && (
            <button type="button" onClick={onApprove} data-testid="admin-student-approve" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-5 py-2 text-xs font-bold hover:bg-emerald-700 transition">
              <CheckCircle2 className="w-3.5 h-3.5" /> Approve — grant Full Access
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function DealsConsole() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | deal
  const [sendingId, setSendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmSendId, setConfirmSendId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/deals")
      .then((r) => setRows(r.data || []))
      .catch((err) => toast.error(formatError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const removeDeal = async (deal) => {
    if (!window.confirm(`Delete "${deal.title}"? This can't be undone.`)) return;
    setDeletingId(deal.id);
    try {
      await api.delete(`/admin/deals/${deal.id}`);
      toast.success("Deal deleted");
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setDeletingId(null); }
  };

  const sendDeal = async (deal) => {
    setSendingId(deal.id);
    setConfirmSendId(null);
    try {
      const { data } = await api.post(`/admin/deals/${deal.id}/send`);
      toast.success(`Sent to ${data.targeted} founders · ${data.in_app_sent} in-app delivered`);
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSendingId(null); }
  };

  const sentCount = rows.filter((r) => r.sent_at).length;
  const draftCount = rows.length - sentCount;

  return (
    <div data-testid="admin-deals-console">
      <WeeklyDigestPanel />
      <CronHealthCard />

      {/* Header + counts */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary inline-flex items-center gap-1.5">
            <Megaphone className="w-3 h-3" /> Broadcast console
          </p>
          <h2 className="font-display text-2xl mt-1">CPG Deals & Broadcasts</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mt-1">
            Draft startup-CPG discounts, perks, and event invites. Save as a draft, then click <b>Send now</b> to broadcast an in-app notification to every founder (respecting their notification preferences).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <p className="text-[11px] text-muted-foreground">
              <b className="text-foreground">{draftCount}</b> draft{draftCount === 1 ? "" : "s"} · <b className="text-foreground">{sentCount}</b> sent
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditing("new")}
            data-testid="admin-deals-new"
            className="btn-primary text-xs inline-flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> New deal
          </button>
        </div>
      </div>

      {/* Composer */}
      {editing && (
        <DealEditor
          deal={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {/* List */}
      {loading && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="admin-deals-loading">Loading deals…</p>
      )}

      {!loading && rows.length === 0 && (
        <div className="surface p-8 text-center" data-testid="admin-deals-empty">
          <Tag className="w-6 h-6 mx-auto text-muted-foreground/50 mb-2" />
          <p className="font-semibold text-sm">No deals yet</p>
          <p className="text-[12px] text-muted-foreground mt-1">Click <b>New deal</b> to compose your first broadcast.</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="grid gap-3">
          {rows.map((deal) => (
            <div
              key={deal.id}
              data-testid={`admin-deal-card-${deal.id}`}
              className="surface p-4 flex items-start gap-4 flex-wrap"
            >
              <div className="flex-1 min-w-[300px]">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${DEAL_KIND_TONE[deal.kind] || DEAL_KIND_TONE.deal}`}>
                    {deal.kind}
                  </span>
                  {deal.sent_at ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                      <Send className="w-2.5 h-2.5" /> Sent · {deal.in_app_sent || 0} in-app
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-shell-muted text-muted-foreground border border-shell-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                      Draft
                    </span>
                  )}
                  {deal.target_package_slug && (
                    <span className="inline-flex items-center rounded-full border border-shell-border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                      {deal.target_package_slug} only
                    </span>
                  )}
                  {deal.ends_at && (
                    <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Ends {fmtDealDate(deal.ends_at)}
                    </span>
                  )}
                </div>
                <h3 className="font-display text-lg" data-testid={`admin-deal-title-${deal.id}`}>{deal.title}</h3>
                {deal.body && (
                  <p className="text-[13px] text-muted-foreground mt-1 line-clamp-2">{deal.body}</p>
                )}
                {deal.cta_url && (
                  <a
                    href={deal.cta_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                  >
                    <ExternalLink className="w-3 h-3" /> {deal.cta_label || "Open link"}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setEditing(deal)}
                  data-testid={`admin-deal-edit-${deal.id}`}
                  className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs font-semibold hover:border-primary/40 transition"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
                {!deal.sent_at ? (
                  <button
                    type="button"
                    onClick={() => setConfirmSendId(deal.id)}
                    disabled={sendingId === deal.id}
                    data-testid={`admin-deal-send-${deal.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-primary text-white px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 transition disabled:opacity-40"
                  >
                    {sendingId === deal.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                    Send now
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmSendId(deal.id)}
                    disabled={sendingId === deal.id}
                    data-testid={`admin-deal-resend-${deal.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs font-semibold hover:border-primary/40 transition disabled:opacity-40"
                    title="Send again to any users who haven't received it yet"
                  >
                    {sendingId === deal.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Re-broadcast
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => removeDeal(deal)}
                  disabled={deletingId === deal.id}
                  data-testid={`admin-deal-delete-${deal.id}`}
                  className="inline-flex items-center rounded-full border border-shell-border p-1.5 text-muted-foreground hover:text-rose-600 hover:border-rose-200 transition disabled:opacity-40"
                  title="Delete"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmSendId && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="admin-deal-send-modal">
          <div className="max-w-md w-full rounded-2xl bg-white shadow-2xl p-6">
            <h3 className="font-display text-xl">Broadcast this deal?</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Every eligible founder will receive an in-app notification. Founders who muted "CPG deals" in their preferences will silently skip.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmSendId(null)}
                className="inline-flex items-center rounded-full border border-shell-border px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
                data-testid="admin-deal-send-modal-cancel"
              >
                Not yet
              </button>
              <button
                type="button"
                onClick={() => sendDeal(rows.find((r) => r.id === confirmSendId))}
                disabled={!!sendingId}
                data-testid="admin-deal-send-modal-confirm"
                className="btn-primary text-xs inline-flex items-center gap-1.5"
              >
                {sendingId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Broadcast now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DealEditor({ deal, onClose, onSaved }) {
  const isNew = !deal;
  const [title, setTitle] = useState(deal?.title || "");
  const [body, setBody] = useState(deal?.body || "");
  const [ctaUrl, setCtaUrl] = useState(deal?.cta_url || "");
  const [ctaLabel, setCtaLabel] = useState(deal?.cta_label || "Grab this deal");
  const [imageUrl, setImageUrl] = useState(deal?.image_url || "");
  const [kind, setKind] = useState(deal?.kind || "deal");
  const [endsAt, setEndsAt] = useState(toDateInputValue(deal?.ends_at));
  const [targetPackage, setTargetPackage] = useState(deal?.target_package_slug || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(), body: body.trim(),
        cta_url: ctaUrl.trim(), cta_label: ctaLabel.trim() || "Grab this deal",
        image_url: imageUrl.trim(), kind,
        ends_at: fromDateInputValue(endsAt),
        target_package_slug: targetPackage || null,
      };
      if (isNew) {
        await api.post("/admin/deals", payload);
        toast.success("Draft saved");
      } else {
        await api.put(`/admin/deals/${deal.id}`, payload);
        toast.success("Deal updated");
      }
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="surface p-5 mb-6" data-testid="admin-deal-editor">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary">
            {isNew ? "New deal" : "Editing deal"}
          </p>
          <h3 className="font-display text-xl mt-0.5">
            {isNew ? "Compose a new broadcast" : deal.title}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid="admin-deal-editor-close"
          className="p-2 rounded hover:bg-shell-muted transition"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-semibold">Title *</span>
          <input
            type="text" value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. 25% off HPP tolling with Sample Co"
            maxLength={140}
            data-testid="admin-deal-title-input"
            className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold">Type</span>
          <select
            value={kind} onChange={(e) => setKind(e.target.value)}
            data-testid="admin-deal-kind-input"
            className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          >
            {DEAL_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
        </label>
      </div>

      <label className="block mt-3">
        <span className="text-xs font-semibold">Body</span>
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)}
          rows={3} maxLength={1500}
          placeholder="Explain what the deal is, who it's from, and how to claim it."
          data-testid="admin-deal-body-input"
          className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary resize-y"
        />
      </label>

      <div className="grid md:grid-cols-2 gap-4 mt-3">
        <label className="block">
          <span className="text-xs font-semibold">CTA URL</span>
          <input
            type="url" value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)}
            placeholder="https://…"
            data-testid="admin-deal-cta-url-input"
            className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold">CTA label</span>
          <input
            type="text" value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)}
            maxLength={60}
            data-testid="admin-deal-cta-label-input"
            className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          />
        </label>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mt-3">
        <label className="block">
          <span className="text-xs font-semibold">Ends on <span className="text-muted-foreground">(optional)</span></span>
          <input
            type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)}
            data-testid="admin-deal-ends-input"
            className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold">Target audience</span>
          <select
            value={targetPackage} onChange={(e) => setTargetPackage(e.target.value)}
            data-testid="admin-deal-target-input"
            className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          >
            <option value="">All founders</option>
            <option value="free">Free Access only</option>
            <option value="founder">Founder Membership only</option>
            <option value="pro">Founder Pro only</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold">Image URL <span className="text-muted-foreground">(optional)</span></span>
          <input
            type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://…"
            data-testid="admin-deal-image-input"
            className="mt-1 w-full rounded-lg border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-primary"
          />
        </label>
      </div>

      <div className="mt-5 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center rounded-full border border-shell-border px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
          data-testid="admin-deal-cancel"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="admin-deal-save"
          className="btn-primary text-xs inline-flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {isNew ? "Save draft" : "Save changes"}
        </button>
      </div>
    </div>
  );
}



function AdminRolesConsole() {
  const [data, setData] = useState({ admins: [], roles: [] });
  const [catalog, setCatalog] = useState({ capabilities: [], role_defaults: {} });
  const [allUsers, setAllUsers] = useState([]);
  const [pickUser, setPickUser] = useState("");
  const [pickRole, setPickRole] = useState("admin");
  const [openCapsFor, setOpenCapsFor] = useState(null); // user_id currently expanded
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [r, c, u] = await Promise.all([
        api.get("/admin/roles/list"),
        api.get("/admin/roles/capabilities/catalog"),
        api.get("/admin/users"),
      ]);
      setData(r.data);
      setCatalog(c.data);
      setAllUsers(u.data || []);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const setRole = async (uid, role) => {
    if (!window.confirm(`Set this user to ${role.replace("_", " ")}? This resets any custom capability overrides to the role defaults.`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/roles/${uid}`, { role });
      toast.success("Role updated");
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const promote = async () => {
    if (!pickUser) return;
    await setRole(pickUser, pickRole);
    setPickUser("");
  };

  const setCapabilities = async (uid, caps) => {
    setBusy(true);
    try {
      await api.post(`/admin/roles/${uid}/capabilities`, { capabilities: caps });
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const resetCapsToDefaults = async (uid) => {
    if (!window.confirm("Reset this user's access to the role defaults?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/roles/${uid}/capabilities`, { capabilities: null });
      toast.success("Reset to role defaults");
      await load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const nonAdminUsers = allUsers.filter(
    (u) => !["super_admin", "admin", "contributor", "tester"].includes((u.role || "").toLowerCase()),
  );

  // Group the capability catalog by section for clean UI rendering.
  const capsByGroup = {};
  (catalog.capabilities || []).forEach((c) => {
    if (!capsByGroup[c.group]) capsByGroup[c.group] = [];
    capsByGroup[c.group].push(c);
  });
  const groups = Object.keys(capsByGroup);

  return (
    <div className="grid gap-6 min-w-0" data-testid="admin-roles-console">
      <div className="card-elevated p-4 sm:p-6 min-w-0">
        <h2 className="font-display text-2xl">Admin Role Hierarchy</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Only Super Admins see this tab. Promote members to Admin or Contributor, then
          expand a row to toggle individual access points on or off.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
          {data.roles.map((r) => (
            <div key={r.key} className="rounded-lg border border-shell-border p-3 bg-white min-w-0" data-testid={`role-info-${r.key}`}>
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{r.label}</p>
              <p className="text-sm mt-1 break-words">{r.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="card-elevated p-4 sm:p-6 min-w-0">
        <h3 className="font-display text-xl">Promote a member</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          <select
            data-testid="admin-roles-pick-user"
            value={pickUser}
            onChange={(e) => setPickUser(e.target.value)}
            className="flex-1 min-w-0 basis-full sm:basis-auto sm:min-w-[220px] rounded-md border border-shell-border bg-white px-3 py-2 text-sm">
            <option value="">Choose a member…</option>
            {nonAdminUsers.map((u) => (
              <option key={u.user_id} value={u.user_id}>{u.email} — {u.name || "(no name)"}</option>
            ))}
          </select>
          <select
            data-testid="admin-roles-pick-role"
            value={pickRole}
            onChange={(e) => setPickRole(e.target.value)}
            className="rounded-md border border-shell-border bg-white px-3 py-2 text-sm">
            <option value="admin">Admin</option>
            <option value="contributor">Contributor</option>
            <option value="tester">Tester (QA)</option>
            <option value="super_admin">Super Admin</option>
          </select>
          <button
            data-testid="admin-roles-promote"
            onClick={promote}
            disabled={!pickUser || busy}
            className="btn-primary disabled:opacity-50">
            {busy ? "Working…" : "Promote"}
          </button>
        </div>
      </div>

      <div className="card-elevated p-0 overflow-hidden min-w-0">
        <div className="px-4 sm:px-6 pt-6 flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-display text-xl">Current admins ({data.admins.length})</h3>
          <p className="text-xs text-muted-foreground">
            Click <span className="font-semibold text-foreground">Access points</span> to toggle individual permissions.
          </p>
        </div>
        <div className="mt-4">
          {data.admins.map((u) => {
            const isOpen = openCapsFor === u.user_id;
            const isSuper = u.role === "super_admin";
            const effective = new Set(u.effective_capabilities || []);
            const customized = Array.isArray(u.capabilities);
            const defaultsForRole = new Set(catalog.role_defaults?.[u.role] || []);

            const toggleCap = (capKey) => {
              const current = new Set(customized ? u.capabilities : defaultsForRole);
              if (current.has(capKey)) current.delete(capKey); else current.add(capKey);
              setCapabilities(u.user_id, Array.from(current));
            };

            return (
              <div key={u.user_id} className="border-t border-shell-border" data-testid={`admin-row-${u.user_id}`}>
                <div className="px-4 sm:px-6 py-3 flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
                  <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                    <p className="font-semibold truncate">{u.name || "—"}</p>
                    <p className="font-mono text-[11px] text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <span className="chip-amber text-[10px]" data-testid={`admin-role-badge-${u.user_id}`}>
                    {u.role.replace("_", " ")}
                  </span>
                  {customized && !isSuper && (
                    <span className="text-[10px] uppercase tracking-widest bg-primary/10 text-primary rounded-full px-2 py-0.5 font-bold"
                          data-testid={`admin-custom-caps-${u.user_id}`}>
                      Custom access
                    </span>
                  )}
                  <button
                    onClick={() => setOpenCapsFor(isOpen ? null : u.user_id)}
                    data-testid={`admin-toggle-caps-${u.user_id}`}
                    disabled={isSuper}
                    className="text-xs px-2 py-1 rounded border border-shell-border hover:bg-shell-tint disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    title={isSuper ? "Super Admins always have every capability" : "Toggle individual access points"}>
                    {isOpen ? "Hide access points" : "Access points"} · {effective.size}
                  </button>
                  <div className="flex flex-wrap gap-1 basis-full sm:basis-auto">
                    {["super_admin", "admin", "contributor", "tester", "member"]
                      .filter((r) => r !== u.role)
                      .map((r) => (
                        <button
                          key={r}
                          onClick={() => setRole(u.user_id, r)}
                          data-testid={`admin-set-${u.user_id}-${r}`}
                          disabled={busy}
                          className="text-[11px] sm:text-xs px-2 py-1 rounded border border-shell-border hover:bg-shell-tint disabled:opacity-50 whitespace-nowrap">
                          {r.replace("_", " ")}
                        </button>
                      ))}
                  </div>
                </div>
                {isOpen && !isSuper && (
                  <div className="px-6 pb-5 pt-1 bg-shell-tint/30" data-testid={`admin-caps-panel-${u.user_id}`}>
                    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                      <p className="text-xs text-muted-foreground max-w-lg">
                        Click a capability to toggle it on or off for <span className="font-semibold text-foreground">{u.email}</span>.
                        Green = granted. Grey = revoked. Role defaults apply until you make a change.
                      </p>
                      {customized && (
                        <button
                          onClick={() => resetCapsToDefaults(u.user_id)}
                          data-testid={`admin-caps-reset-${u.user_id}`}
                          disabled={busy}
                          className="text-xs font-semibold text-muted-foreground hover:text-foreground border border-shell-border rounded-full px-3 py-1">
                          Reset to {u.role.replace("_", " ")} defaults
                        </button>
                      )}
                    </div>
                    <div className="grid gap-4">
                      {groups.map((g) => (
                        <div key={g}>
                          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">{g}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {capsByGroup[g].map((c) => {
                              const on = effective.has(c.key);
                              return (
                                <button
                                  key={c.key}
                                  onClick={() => toggleCap(c.key)}
                                  disabled={busy}
                                  title={c.description}
                                  data-testid={`admin-cap-${u.user_id}-${c.key}`}
                                  className={[
                                    "text-xs px-2.5 py-1.5 rounded-full border font-semibold transition disabled:opacity-50",
                                    on
                                      ? "border-green-500 bg-green-50 text-green-700 hover:bg-green-100"
                                      : "border-shell-border bg-white text-muted-foreground hover:bg-shell-tint hover:text-foreground",
                                  ].join(" ")}>
                                  {on ? "✓" : "○"} {c.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}